import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  DialogTitle,
  Divider,
  Dropdown,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Tab,
  TabList,
  Tabs,
  Typography,
} from '@mui/joy';
import type { IconType } from 'react-icons';
import {
  FaCheck,
  FaEllipsisV,
  FaLink,
  FaPaperPlane,
  FaPlus,
  FaTimes,
} from 'react-icons/fa';
import {
  MdOutlineDescription,
  MdReceiptLong,
  MdRequestQuote,
} from 'react-icons/md';
import { useSearchParams } from 'react-router-dom';
import { IconWrapper } from '../../components/IconWrapper';
import { DocumentLinker } from '../../components/DocumentLinker';
import { InvoiceLinker } from '../../components/InvoiceLinker';
import ProjectFolderViewer from '../../components/ProjectFolderViewer';
import EstimateQuoteWizard, { type WizardLeaveHandle } from '../../modules/docs/pages/EstimateQuoteWizard';
import type { DocumentLineInput } from '../../modules/docs/types/documents.types';
import { visualDesignerSurveysService, type FinalSurveyDeltaRecord, type VisualDesignerSurvey } from '../../modules/docs/services/visualDesignerSurveys.service';
import { FinalSurveyReviewTable } from '../../modules/docs/components/FinalSurveyReviewTable';
import {
  finalSurveyOperationalState,
  finalSurveyReviewIntro,
  isFinalSurveyOperational,
} from '../../modules/docs/utils/surveyDelta';
import {
  computeInvoiceDelta,
  invoiceTabCreateButton,
  documentDisplayRef,
  invoiceDisplayStatus,
  invoiceRoleLabel,
  normalizeInvoiceRole,
  manualAdjustmentTotal,
  reissuedFromInvoiceId,
  totalBalanceDeductions,
  type BalanceAdjustment,
  type InvoiceRole,
} from '../../modules/docs/utils/invoiceLedger';
import { InvoiceSequencePanel } from './InvoiceSequencePanel';
import { PaymentScheduleCard } from './PaymentScheduleCard';
import {
  invoiceDeleteAllowedWithSchedule,
  nextIssueAmount,
  presentScheduleStages,
} from './paymentSchedule';
import { paymentScheduleService } from './paymentSchedule.service';
import type { PaymentSchedule, ScheduleInvoice } from './paymentSchedule.types';
import {
  FINAL_IN_FLIGHT_TAB_ALERT,
  FINAL_SURVEY_HISTORICAL_AFTER_BALANCE,
  finalReopenConfirmCopy,
  isFinalBalanceIssued,
  isInFlightFinalReapproval,
  isUnresolvedPendingReviewDelta,
} from '../../modules/docs/utils/surveyFinalLifecycle';
import { reviewSectionsFromSnapshot } from '../../modules/docs/utils/surveyDeltaReview';
import { type VersionLockInfo } from './WorkspaceVisualDesignerCard';
import DocumentDetailPage from '../../modules/docs/pages/DocumentDetailPage';
import { documentsService } from '../../modules/docs/services/documents.service';
import { directSupabase } from '../../lib/supabaseClient';
import { sendDocumentToCustomer } from '../../modules/docs/services/sendDocumentToCustomer.service';
import { reissueBalanceInvoice } from './balanceReissue.service';
import { isDocumentUnassigned } from '../../services/documentAssignment';
import { ensureSignedIn, GRAPH_SIGNIN_REQUIRED_TO_SEND } from '../../utils/graphService';
import type { Document, DocumentKind, DocumentStatus } from '../../modules/docs/types/documents.types';
import { getAvailableStatuses } from '../../modules/docs/types/documents.types';
import type { Customer } from '../../services/customerService';
import type { Project } from '../../types/project';
import WorkspaceVisualDesignerCard from './WorkspaceVisualDesignerCard';
import {
  primarySurvey,
  canProgressUnsentEstimate,
  draftQuoteTitle,
  isAcceptedOrCashStatus,
  firstSurveyStatusChip,
  finalSurveyStatusChip,
  commercialDocumentStatusLabel,
  commercialDocumentStatusCaption,
  resolveCommercialFinalAcceptance,
  type CommercialFinalAcceptance,
} from './workspaceQuote.helpers';
import { deleteWorkspaceCommercialDocument, deleteWorkspaceInvoice } from './workspaceQuote.service';
import {
  commercialDocumentDeleteBlockedReason,
  finalSurveyTabAvailable,
  finalSurveyWorkPhase,
  firstSurveyHasBeenTransmitted,
  firstSurveyHistoricalLockReason,
  firstSurveyIsHistorical,
  QUOTE_STATUS_REVERT_BLOCKED_MESSAGE,
  quoteStatusRevertBlocked,
  surveySheetDeleteBlockedReason,
} from '../../modules/docs/utils/surveySheetDelete';
import WorkspaceTransmissionsCard from './WorkspaceTransmissionsCard';
import SurveyTransmissionMismatchAlert from './SurveyTransmissionMismatchAlert';
import { WorkspaceCardHeader } from '../../components/WorkspaceCardHeader';
import {
  BILLING_DETAILS_REQUIRED_MESSAGE,
  assertCanIssueCommercialDocument,
  resolveDocumentCustomerIdentity,
  workspaceBillingDetailsAreValid,
} from './workspaceBillingIdentity';

interface QuotesStagePanelProps {
  project: Project;
  customer: Customer | null;
  isAdmin?: boolean;
  section?: 'quotes' | 'invoices';
  leaveGuardRef?: React.MutableRefObject<QuotesLeaveHandle | null>;
}

export type QuotesLeaveHandle = {
  requestLeave: (proceed: () => void) => void;
};

type QuotePane = 'first' | 'final';

type ConfirmDialogState = {
  title: string;
  body: string;
  alert?: string | null;
  confirmLabel?: string;
  confirmColor?: 'primary' | 'danger' | 'warning' | 'success';
  onConfirm: () => void | Promise<void>;
};

type NoticeDialogState = {
  title: string;
  body: string;
  url?: string | null;
};

type PanelMode =
  | { type: 'idle' }
  | { type: 'view'; id: string }
  | { type: 'edit'; id: string }
  | {
    type: 'create';
    kind: DocumentKind;
    lifecycleRef?: string;
    invoiceRole?: InvoiceRole;
    preferredTitle?: string;
    seedLines?: DocumentLineInput[];
  };

type ActiveVersionSummary = {
  versionId: string;
  versionNumber: number;
  isTransmitted: boolean;
  overrideUnlocked: boolean;
  title?: string | null;
};

function formatMoney(value?: number | null) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(Number(value));
}

function toScheduleInvoices(docs: Document[]): ScheduleInvoice[] {
  return docs.map((doc) => ({
    id: doc.id,
    role: doc.invoiceRole,
    status: doc.status,
    grossTotal: doc.grossTotal,
    depositAmount: doc.depositAmount,
    createdAt: doc.createdAt,
  }));
}

function formatDocDate(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function latestDocument(docs: Document[]) {
  return [...docs].sort((a, b) => (Date.parse(b.updatedAt || b.createdAt) || 0) - (Date.parse(a.updatedAt || a.createdAt) || 0))[0];
}

function statusMeta(
  doc: Document,
  finalAcceptance?: CommercialFinalAcceptance,
  customerTransmitted?: boolean,
): { label: string; caption: string; color: 'success' | 'primary' | 'neutral' | 'danger'; icon?: IconType } {
  const status = String(doc.status || '').trim().toLowerCase() as DocumentStatus;
  if (doc.kind === 'Invoice') {
    const chip = invoiceDisplayStatus({ status, customerTransmitted });
    return {
      label: chip.label,
      caption: chip.label === 'Sent' ? (formatDocDate(doc.sentAt || doc.statusChangedAt) || '') : '',
      color: chip.color,
      icon: chip.label === 'Sent' ? FaPaperPlane : (chip.color === 'success' ? FaCheck : undefined),
    };
  }
  const overlay = finalAcceptance
    ? { ...finalAcceptance, date: formatDocDate(finalAcceptance.date) || finalAcceptance.date }
    : undefined;
  if (status === 'accepted') {
    const date = formatDocDate(doc.acceptedAt || doc.statusChangedAt);
    return {
      label: commercialDocumentStatusLabel(doc.kind, status, overlay),
      caption: commercialDocumentStatusCaption(doc.kind, status, date, overlay) || '',
      color: 'success',
      icon: FaCheck,
    };
  }
  if (status === 'sent') {
    const date = formatDocDate(doc.sentAt || doc.statusChangedAt);
    return {
      label: commercialDocumentStatusLabel(doc.kind, status),
      caption: commercialDocumentStatusCaption(doc.kind, status, date) || '',
      color: 'primary',
      icon: FaPaperPlane,
    };
  }
  if (status === 'paid' || status === 'cash') {
    const date = formatDocDate(doc.paidAt || doc.statusChangedAt);
    return {
      label: commercialDocumentStatusLabel(doc.kind, status, overlay),
      caption: commercialDocumentStatusCaption(doc.kind, status, date, overlay) || date,
      color: 'success',
      icon: FaCheck,
    };
  }
  if (status === 'rejected') {
    const date = formatDocDate(doc.rejectedAt || doc.statusChangedAt);
    return {
      label: commercialDocumentStatusLabel(doc.kind, status),
      caption: commercialDocumentStatusCaption(doc.kind, status, date) || date,
      color: 'danger',
      icon: FaTimes,
    };
  }
  if (status === 'invoiced') {
    return { label: 'Invoiced', caption: formatDocDate(doc.invoicedAt || doc.statusChangedAt), color: 'primary' };
  }
  if (status === 'void') {
    return { label: 'Void', caption: formatDocDate(doc.voidedAt || doc.statusChangedAt), color: 'neutral' };
  }
  return { label: commercialDocumentStatusLabel(doc.kind, status), caption: formatDocDate(doc.issuedAt || doc.updatedAt), color: 'neutral' };
}

function kindNoun(kind: DocumentKind) {
  if (kind === 'Quotation') return 'Quotation';
  if (kind === 'Invoice') return 'Invoice';
  return 'Estimate';
}

function looksLikeVersionLabel(value?: string | null) {
  return /^\d+$/.test(String(value || '').trim());
}

function documentIdentity(doc: Document, versionTitle?: string | null) {
  const title = String(versionTitle || doc.title || '').trim();
  const subtitleTitle = title && (doc.kind === 'Invoice' || !looksLikeVersionLabel(title)) ? title : null;
  return { subtitleTitle };
}

function CandidateRow({
  doc,
  versionTitle,
  trailing,
}: {
  doc: Document;
  versionTitle?: string | null;
  trailing?: React.ReactNode;
}) {
  const title = String(versionTitle || doc.title || '').trim();
  const subtitleTitle = title && (doc.kind === 'Invoice' || !looksLikeVersionLabel(title)) ? title : null;
  return (
    <Box
      sx={{
        p: 1.25,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 'sm',
      }}
    >
      <Stack direction="row" justifyContent="space-between" gap={1} flexWrap="wrap" alignItems="center">
        <Box>
          <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
            <Typography level="body-sm" fontWeight="lg">
              {documentDisplayRef(doc)}
            </Typography>
          </Stack>
          {subtitleTitle && (
            <Typography level="body-xs" sx={{ color: 'neutral.500' }} noWrap>
              {subtitleTitle}
            </Typography>
          )}
          <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
            {doc.kind} · {doc.status} · {formatMoney(doc.grossTotal)}
          </Typography>
        </Box>
        <Stack direction="row" gap={1} alignItems="center">
          {trailing}
        </Stack>
      </Stack>
    </Box>
  );
}

const QuotesStagePanel: React.FC<QuotesStagePanelProps> = ({
  project,
  customer,
  isAdmin = false,
  section = 'quotes',
  leaveGuardRef,
}) => {
  const [searchParams] = useSearchParams();
  const focusDocId = String(searchParams.get('doc') || '').trim();
  const quoteLinkerRef = useRef<{ openModal: () => void }>(null);
  const invoiceLinkerRef = useRef<{ openModal: () => void }>(null);
  const wizardLeaveRef = useRef<WizardLeaveHandle | null>(null);
  const [estimates, setEstimates] = useState<Document[]>([]);
  const [quotes, setQuotes] = useState<Document[]>([]);
  const [invoices, setInvoices] = useState<Document[]>([]);
  const [balanceAdjustments, setBalanceAdjustments] = useState<Record<string, BalanceAdjustment>>({});
  const [paymentSchedule, setPaymentSchedule] = useState<PaymentSchedule | null>(null);
  const [scheduleTick, setScheduleTick] = useState(0);
  const [candidateQuotes, setCandidateQuotes] = useState<Document[]>([]);
  const [candidateInvoices, setCandidateInvoices] = useState<Document[]>([]);
  const [versionById, setVersionById] = useState<Record<string, ActiveVersionSummary>>({});
  const [versionLockById, setVersionLockById] = useState<Record<string, { isTransmitted: boolean; overrideUnlocked: boolean }>>({});
  const [lockRefreshKey, setLockRefreshKey] = useState(0);
  const [lockDialog, setLockDialog] = useState<{
    documentId: string;
    versionId: string;
    label: string;
    mode: 'unlock' | 'relock';
  } | null>(null);
  const [lockReason, setLockReason] = useState('');
  const [lockBusy, setLockBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [panel, setPanel] = useState<PanelMode>({ type: 'idle' });
  const [advancedLinkingOpen, setAdvancedLinkingOpen] = useState(false);
  const [invoiceChoiceOpen, setInvoiceChoiceOpen] = useState(false);
  const [adjustmentChoiceOpen, setAdjustmentChoiceOpen] = useState(false);
  const [standaloneChoiceOpen, setStandaloneChoiceOpen] = useState(false);
  const [invoiceSourceQuoteId, setInvoiceSourceQuoteId] = useState<string | null>(null);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
  const [quotePane, setQuotePane] = useState<QuotePane>('first');
  const [sendingQuote, setSendingQuote] = useState(false);
  const [statusBusyId, setStatusBusyId] = useState<string | null>(null);
  const [exportPdfSignal, setExportPdfSignal] = useState(0);
  const [exportPdfDocId, setExportPdfDocId] = useState<string | null>(null);
  const [vdVersionLock, setVdVersionLock] = useState<VersionLockInfo | null>(null);
  const [vdSurvey, setVdSurvey] = useState<VisualDesignerSurvey | null>(null);
  const [vdLines, setVdLines] = useState<DocumentLineInput[]>([]);
  const [surveys, setSurveys] = useState<VisualDesignerSurvey[]>([]);
  const [vdSurveyId, setVdSurveyId] = useState('');
  const [surveyActionBusy, setSurveyActionBusy] = useState(false);
  const [finalDelta, setFinalDelta] = useState<FinalSurveyDeltaRecord | null>(null);
  const [reviewDeltaOpen, setReviewDeltaOpen] = useState(false);
  const [reapprovalBusy, setReapprovalBusy] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(null);
  const [noticeDialog, setNoticeDialog] = useState<NoticeDialogState | null>(null);
  const [visualDesignerExpanded, setVisualDesignerExpanded] = useState(false);
  const [quoteDetailsExpanded, setQuoteDetailsExpanded] = useState(true);
  const [invoiceDetailsExpanded, setInvoiceDetailsExpanded] = useState(true);
  const [pageSaving, setPageSaving] = useState(false);
  const isQuotesSection = section === 'quotes';
  const billingBlocked = !workspaceBillingDetailsAreValid({ project, customer });
  const commercialIdentity = resolveDocumentCustomerIdentity({
    customer,
    project,
    kind: 'Quotation',
  });

  const quoteBlocksEstimate = quotes.length > 0 && estimates.length === 0;
  const selectedId = panel.type === 'view' || panel.type === 'edit' ? panel.id : null;

  const requestLeave = useCallback((proceed: () => void) => {
    const wizardOpen = panel.type === 'create' || panel.type === 'edit';
    if (wizardOpen && wizardLeaveRef.current) {
      wizardLeaveRef.current.requestLeave(proceed);
      return;
    }
    proceed();
  }, [panel]);

  const changePanel = (next: PanelMode) => {
    requestLeave(() => setPanel(next));
  };

  const openVisualDesigner = () => {
    const quote = latestDocument(quotes);
    if (!quote) return;
    const final = primarySurvey(surveys, 'final');
    setQuotePane(final ? 'final' : 'first');
    // Surveys are edited in their own tabs — no need to open the quote wizard.
    changePanel({ type: 'view', id: quote.id });
  };

  useLayoutEffect(() => {
    if (!leaveGuardRef) return;
    leaveGuardRef.current = { requestLeave };
    return () => {
      leaveGuardRef.current = null;
    };
  }, [leaveGuardRef, requestLeave]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [projectDocs, customerDocs] = await Promise.all([
        documentsService.getDocumentsByProject(project.id),
        project.customer_id
          ? documentsService.getDocumentsForCustomer(project.customer_id)
          : Promise.resolve([]),
      ]);
      let nextEstimates = projectDocs.filter((doc) => doc.kind === 'Estimate');
      let nextQuotes = projectDocs.filter((doc) => doc.kind === 'Quotation');
      if (isQuotesSection && nextQuotes.length === 0) {
        const acceptedEstimate = nextEstimates.find((doc) => {
          const status = String(doc.status || '').trim().toLowerCase();
          return status === 'accepted' || status === 'cash';
        });
        if (acceptedEstimate) {
          const promoted = await documentsService.progressAcceptedEstimateToQuote(acceptedEstimate.id).catch((err) => {
            console.warn('Failed to auto-progress accepted estimate to quote', err);
            return null;
          });
          if (promoted?.id) {
            const refreshed = await documentsService.getDocumentsByProject(project.id);
            nextEstimates = refreshed.filter((doc) => doc.kind === 'Estimate');
            nextQuotes = refreshed.filter((doc) => doc.kind === 'Quotation');
            projectDocs.splice(0, projectDocs.length, ...refreshed);
            setQuotePane('final');
            setPanel({ type: 'view', id: promoted.id });
          }
        }
      }
      const nextInvoices = projectDocs.filter((doc) => doc.kind === 'Invoice');
      const linkedIds = new Set(projectDocs.map((doc) => doc.id));
      const customerDocsTyped = customerDocs as Document[];
      // A reissued balance carries its deductions and points back to the voided original
      const reissued = nextInvoices.filter((doc) => reissuedFromInvoiceId(doc.sourceSurveyEvent));
      const [deductionsById, originals] = await Promise.all([
        documentsService.listBalanceDeductions(reissued.map((doc) => doc.id)).catch(() => ({})),
        Promise.all(reissued.map((doc) => documentsService
          .getDocument(String(reissuedFromInvoiceId(doc.sourceSurveyEvent)))
          .catch(() => null))),
      ]);
      const nextAdjustments: Record<string, BalanceAdjustment> = {};
      reissued.forEach((doc, index) => {
        const original = originals[index];
        nextAdjustments[doc.id] = {
          deductions: (deductionsById as Record<string, BalanceAdjustment['deductions']>)[doc.id] || [],
          replacesRef: original ? documentDisplayRef(original) || null : null,
        };
      });
      setEstimates(nextEstimates);
      setQuotes(nextQuotes);
      setInvoices(nextInvoices);
      setBalanceAdjustments(nextAdjustments);
      const nextCandidateQuotes = customerDocsTyped
        .filter((doc) => doc.kind === 'Estimate' || doc.kind === 'Quotation')
        .filter((doc) => !linkedIds.has(doc.id) && isDocumentUnassigned(doc.projectId));
      setCandidateQuotes(nextCandidateQuotes);
      setCandidateInvoices(customerDocsTyped.filter((doc) => (
        doc.kind === 'Invoice' && !linkedIds.has(doc.id) && isDocumentUnassigned(doc.projectId)
      )));
      try {
        setVersionById(await documentsService.getActiveVersionSummaries([
          ...nextEstimates,
          ...nextQuotes,
          ...nextCandidateQuotes,
        ].map((doc) => doc.id)));
      } catch (err) {
        console.warn('Failed to load document versions', err);
        setVersionById({});
      }
      if (isQuotesSection && nextQuotes.length > 0) {
        await visualDesignerSurveysService.ensureFirstSurveyBoundToQuote(project.id, nextQuotes[0].id).catch((err) => {
          console.warn('Failed to ensure First Survey', err);
        });
      }
      // Final is provisioned on First transmit (not on accept). Do not auto-backfill here.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load documents');
    } finally {
      setLoading(false);
      setLoadedOnce(true);
    }
  }, [project.id, project.customer_id, isQuotesSection]);

  useEffect(() => {
    setLoadedOnce(false);
    load();
  }, [load]);

  useEffect(() => {
    if (panel.type !== 'edit') setVdVersionLock(null);
  }, [panel.type]);

  useEffect(() => {
    let cancelled = false;
    visualDesignerSurveysService
      .listByProject(project.id)
      .then((rows) => {
        if (!cancelled) setSurveys(rows);
      })
      .catch((err) => {
        console.warn('Failed to load Visual Designer surveys', err);
        if (!cancelled) setSurveys([]);
      });
    return () => {
      cancelled = true;
    };
  }, [project.id, panel.type, quotePane]);

  useEffect(() => {
    if (!surveys.length) {
      setVdSurveyId('');
      return;
    }
    setVdSurveyId((prev) => {
      const first = primarySurvey(surveys, 'first');
      if (vdVersionLock) {
        const bound = surveys.find(
          (s) => s.documentVersionId === vdVersionLock.id || s.id === vdVersionLock.visualSurveyId
        );
        if (bound) return bound.id;
      }
      if (first) return first.id;
      const previous = surveys.find((s) => s.id === prev);
      if (previous) return previous.id;
      return surveys[0].id;
    });
  }, [surveys, vdVersionLock]);

  useEffect(() => {
    if (!vdSurveyId) {
      setVdSurvey(null);
      setVdLines([]);
      return;
    }
    const listed = surveys.find((s) => s.id === vdSurveyId) || null;
    if (listed) setVdSurvey(listed);
    let cancelled = false;
    visualDesignerSurveysService
      .listItems(vdSurveyId)
      .then((items) => {
        if (!cancelled) setVdLines(items);
      })
      .catch((err) => {
        console.warn('Failed to load Visual Designer survey', err);
        if (!cancelled) {
          setVdSurvey(null);
          setVdLines([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [vdSurveyId, surveys, quotePane]);

  const quoteRefs = new Set(quotes.map((doc) => doc.lifecycleRef));

  const requestExportPdf = (doc: Document) => {
    setExportPdfDocId(doc.id);
    setExportPdfSignal((value) => value + 1);
    changePanel({ type: 'view', id: doc.id });
  };

  const handleExportPdfTriggered = useCallback(() => {
    setExportPdfDocId(null);
  }, []);

  const toggleView = (doc: Document) => {
    if ((panel.type === 'view' || panel.type === 'edit') && panel.id === doc.id) {
      changePanel({ type: 'idle' });
      return;
    }
    changePanel({ type: 'view', id: doc.id });
  };

  useEffect(() => {
    if (loading || !focusDocId) return;
    const pool = isQuotesSection ? [...quotes, ...estimates] : invoices;
    if (!pool.some((doc) => doc.id === focusDocId)) return;
    setPanel((current) => {
      if (current.type === 'edit') return current;
      if (current.type === 'view' && current.id === focusDocId) return current;
      return { type: 'view', id: focusDocId };
    });
  }, [estimates, focusDocId, invoices, isQuotesSection, loading, quotes]);

  const updateDocStatus = async (doc: Document, nextStatus: DocumentStatus) => {
    const normalizedStatus = String(nextStatus || '').trim().toLowerCase() as DocumentStatus;
    if (normalizedStatus === String(doc.status || '').trim().toLowerCase()) return;
    if (quoteStatusRevertBlocked({
      documentKind: doc.kind,
      fromStatus: doc.status,
      toStatus: normalizedStatus,
      finalWorkPhase: finalSurveyWorkPhase(primarySurvey(surveys, 'final')),
    })) {
      setError(QUOTE_STATUS_REVERT_BLOCKED_MESSAGE);
      return;
    }
    setConfirmDialog({
      title: 'Change status',
      body: `Change ${documentDisplayRef(doc)} status to ${normalizedStatus}?`,
      confirmLabel: 'Change status',
      onConfirm: () => { void applyDocStatus(doc, normalizedStatus); },
    });
  };

  const applyDocStatus = async (doc: Document, normalizedStatus: DocumentStatus) => {
    setStatusBusyId(doc.id);
    setError(null);
    try {
      const updated = await documentsService.updateDocument(doc.id, { status: normalizedStatus });
      const provisionError = (updated as Document & { finalSurveyProvisionError?: string }).finalSurveyProvisionError;
      const progressQuoteError = (updated as Document & { progressQuoteError?: string }).progressQuoteError;
      const progressedQuoteId = (updated as Document & { progressedQuoteId?: string }).progressedQuoteId;
      if ((doc.kind === 'Quotation' || doc.kind === 'Estimate') && normalizedStatus === 'accepted') {
        try {
          await documentsService.ensureDepositInvoice(doc.id);
        } catch (invoiceErr) {
          console.error('Failed to create deposit invoice after accept', invoiceErr);
          setError(invoiceErr instanceof Error ? invoiceErr.message : 'Status updated, but deposit invoice creation failed');
        }
      }
      if (
        (doc.kind === 'Quotation' || doc.kind === 'Estimate')
        && (normalizedStatus === 'accepted' || normalizedStatus === 'cash')
      ) {
        const viewId = progressedQuoteId || doc.id;
        changePanel({ type: 'view', id: viewId });
        setQuotePane('final');
        try {
          // Legacy ensure only — no-op when Final already exists from First transmit.
          if (!primarySurvey(surveys, 'final')) {
            await visualDesignerSurveysService.createFinalSurveyFromFirst(project.id);
          }
        } catch (surveyErr) {
          console.error('Legacy Final Survey ensure failed', surveyErr);
          setError(
            surveyErr instanceof Error
              ? surveyErr.message
              : (provisionError || 'Status updated, but Final Survey could not be ensured'),
          );
        }
        if (progressQuoteError) {
          setError(progressQuoteError);
        }
      }
      await load();
      await refreshSurveys();
      if (
        (doc.kind === 'Quotation' || doc.kind === 'Estimate')
        && (normalizedStatus === 'accepted' || normalizedStatus === 'cash')
      ) {
        setQuotePane('final');
        if (progressedQuoteId) changePanel({ type: 'view', id: progressedQuoteId });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setStatusBusyId(null);
    }
  };

  const assignToProject = async (doc: Document) => {
    if (!isDocumentUnassigned(doc.projectId) && String(doc.projectId) !== String(project.id)) {
      setError('This document is already linked to another workspace.');
      return;
    }
    if (doc.kind === 'Quotation' && quotes.length > 0) {
      setError('This workspace already has a quote. Use Clone workspace if you need another quote under a new lifecycle ref.');
      return;
    }
    if (doc.kind === 'Estimate' && estimates.length > 0) {
      setError('This workspace already has an estimate.');
      return;
    }
    if (doc.kind === 'Estimate' && quotes.length > 0) {
      setError('An estimate cannot be linked while a quote exists on this workspace.');
      return;
    }
    setAssigningId(doc.id);
    setError(null);
    try {
      await documentsService.updateDocument(doc.id, { projectId: project.id });
      if (doc.kind === 'Quotation') {
        await visualDesignerSurveysService.ensureFirstSurvey(project.id).catch((err) => {
          console.warn('Failed to ensure First Survey', err);
        });
      }
      changePanel({ type: 'view', id: doc.id });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link document');
    } finally {
      setAssigningId(null);
    }
  };

  const deleteCommercialDocument = async (doc: Document) => {
    if (doc.kind !== 'Quotation' && doc.kind !== 'Estimate') return;
    const blocked = commercialDocumentDeleteBlockedReason(firstSurveyHasBeenTransmitted({
      anyCommercialVersionIsTransmitted: Object.values(versionLockById).some((lock) => lock.isTransmitted),
      hasFinalSurvey: !!primarySurvey(surveys, 'final'),
    }));
    if (blocked) {
      setNoticeDialog({ title: 'Cannot delete', body: blocked });
      return;
    }
    const noun = doc.kind === 'Quotation' ? 'quote' : 'estimate';
    setConfirmDialog({
      title: `Delete ${noun}`,
      body:
        `Permanently delete ${doc.lifecycleRef}?\n\n`
        + `This removes this ${noun} and its version history from this workspace, and clears First/Final Survey so you can start blank.\n\n`
        + 'Invoices and other workspaces are not affected.',
      confirmLabel: `Delete ${noun}`,
      confirmColor: 'danger',
      onConfirm: () => { void applyDeleteCommercialDocument(doc); },
    });
  };

  const applyDeleteCommercialDocument = async (doc: Document) => {
    if (doc.kind !== 'Quotation' && doc.kind !== 'Estimate') return;
    const noun = doc.kind === 'Quotation' ? 'quote' : 'estimate';
    const blocked = commercialDocumentDeleteBlockedReason(firstSurveyHasBeenTransmitted({
      anyCommercialVersionIsTransmitted: Object.values(versionLockById).some((lock) => lock.isTransmitted),
      hasFinalSurvey: !!primarySurvey(surveys, 'final'),
    }));
    if (blocked) {
      setError(blocked);
      return;
    }
    setDeletingDocId(doc.id);
    setError(null);
    try {
      await deleteWorkspaceCommercialDocument({ documentId: doc.id, projectId: project.id });
      if (selectedId === doc.id) changePanel({ type: 'idle' });
      setQuotePane('first');
      await load();
      await refreshSurveys();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to delete ${noun}`);
    } finally {
      setDeletingDocId(null);
    }
  };

  const deleteInvoiceDocument = async (doc: Document) => {
    if (doc.kind !== 'Invoice') return;
    if (!invoiceDeleteAllowedWithSchedule({
      invoice: doc,
      invoices,
      stages: paymentSchedule?.stages,
    })) return;
    setConfirmDialog({
      title: 'Delete invoice',
      body:
        `Permanently delete invoice ${documentDisplayRef(doc)}?\n\n`
        + 'This removes the invoice and its lines from this workspace with no residue. Quotes, estimates, and surveys are not affected.\n\n'
        + 'This action cannot be undone.',
      confirmLabel: 'Delete invoice',
      confirmColor: 'danger',
      onConfirm: () => { void applyDeleteInvoiceDocument(doc); },
    });
  };

  const applyDeleteInvoiceDocument = async (doc: Document) => {
    if (doc.kind !== 'Invoice') return;
    if (!invoiceDeleteAllowedWithSchedule({
      invoice: doc,
      invoices,
      stages: paymentSchedule?.stages,
    })) return;
    setDeletingDocId(doc.id);
    setError(null);
    try {
      await deleteWorkspaceInvoice({ documentId: doc.id, projectId: project.id });
      const remainingInvoices = invoices.filter((invoice) => invoice.id !== doc.id);
      await paymentScheduleService.rollbackInvoice({
        projectId: project.id,
        invoiceId: doc.id,
        invoicesAfterDelete: toScheduleInvoices(remainingInvoices),
        grossTotal: ledgerGross,
      }).catch((err) => {
        console.warn('Failed to roll back Payment Schedule', err);
      });
      if (selectedId === doc.id) changePanel({ type: 'idle' });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete invoice');
    } finally {
      setDeletingDocId(null);
    }
  };

  const requestReissueBalance = (doc: Document) => {
    const ref = documentDisplayRef(doc);
    setConfirmDialog({
      title: 'Reissue balance',
      body:
        `Reissue balance ${ref}?\n\n`
        + `${ref} is voided and kept on record, and its customer link stops working. A replacement balance `
        + 'is raised under the next invoice reference for the same amount, as a draft. Add any agreed '
        + 'deduction to it, then send it to FreeAgent.\n\n'
        + 'If the original is still in FreeAgent, delete it there first.',
      confirmLabel: 'Reissue balance',
      onConfirm: () => { void applyReissueBalance(doc); },
    });
  };

  const applyReissueBalance = async (doc: Document) => {
    setDeletingDocId(doc.id);
    setError(null);
    try {
      const replacement = await reissueBalanceInvoice(doc.id);
      await load();
      changePanel({ type: 'view', id: replacement.id });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reissue the balance');
    } finally {
      setDeletingDocId(null);
    }
  };

  const saveBalanceDeductions = async (doc: Document, deductions: Array<{ name: string; amount: number }>) => {
    await documentsService.setBalanceDeductions(doc.id, deductions);
    await load();
  };

  const openLockDialog = (doc: Document, mode: 'unlock' | 'relock') => {
    const summary = versionById[doc.id];
    if (!summary?.isTransmitted) return;
    // Quote-lane unlock targets the active version. If that is still First and Final exists, block.
    if (mode === 'unlock' && firstIsHistorical) {
      const activeId = String(summary.versionId || '').trim();
      const firstVid = String(firstSurvey?.documentVersionId || '').trim();
      if (activeId && firstVid && activeId === firstVid) {
        setError(firstHistoricalReason || 'First Survey is historical.');
        return;
      }
    }
    setError(null);
    setLockReason('');
    setLockDialog({
      documentId: doc.id,
      versionId: summary.versionId,
      label: doc.lifecycleRef,
      mode,
    });
  };

  const closeLockDialog = () => {
    if (lockBusy) return;
    setLockDialog(null);
    setLockReason('');
  };

  const submitLockDialog = async () => {
    if (!lockDialog) return;
    setLockBusy(true);
    setError(null);
    try {
      if (lockDialog.mode === 'unlock') {
        await documentsService.unlockVersionOverride(lockDialog.versionId, lockReason);
      } else {
        await documentsService.relockVersionOverride(lockDialog.versionId);
      }
      setLockDialog(null);
      setLockReason('');
      await load();
      await refreshSurveys();
      await refreshSurveyVersionLocks();
      setLockRefreshKey((value) => value + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update quote lock');
    } finally {
      setLockBusy(false);
    }
  };

  const handleDocumentReady = async (documentId: string) => {
    const arrivedFromCreate = panel.type === 'create';
    try {
      const saved = await documentsService.getDocument(documentId).catch(() => null);
      if (saved?.kind === 'Quotation') {
        await visualDesignerSurveysService.ensureFirstSurveyBoundToQuote(project.id, documentId).catch((err) => {
          console.warn('Failed to ensure First Survey binding', err);
        });
        if (arrivedFromCreate) setQuotePane('first');
      }
      // After a successful save/create, show the read-only quote view.
      setPanel({ type: 'view', id: documentId });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Document saved, but the workspace list failed to refresh');
      setPanel({ type: 'view', id: documentId });
    }
  };

  const progressEstimate = async (doc: Document) => {
    if (quotes.length > 0) {
      setError('This workspace already has a quote. Use Clone workspace if you need another quote under a new lifecycle ref.');
      return;
    }
    setAssigningId(doc.id);
    setError(null);
    try {
      const created = await documentsService.cloneToQuotation(doc.id);
      if (created.projectId !== project.id) {
        await documentsService.updateDocument(created.id, { projectId: project.id });
      }
      await visualDesignerSurveysService.ensureFirstSurvey(project.id).catch((err) => {
        console.warn('Failed to ensure First Survey', err);
      });
      setQuotePane('first');
      changePanel({ type: 'view', id: created.id });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to progress estimate to quote');
    } finally {
      setAssigningId(null);
    }
  };

  const startCreate = (kind: DocumentKind, lifecycleRef?: string) => {
    if (!project.customer_id) {
      setError('Assign a customer in Overview before creating documents.');
      return;
    }
    try {
      assertCanIssueCommercialDocument({ project, customer, kind });
    } catch (err) {
      setError(err instanceof Error ? err.message : BILLING_DETAILS_REQUIRED_MESSAGE);
      return;
    }
    if (kind === 'Estimate' && estimates.length > 0) {
      setError('This workspace already has an estimate.');
      return;
    }
    if (kind === 'Estimate' && quotes.length > 0) {
      setError('An estimate cannot be added while a quote exists on this workspace.');
      return;
    }
    if (kind === 'Quotation' && quotes.length > 0) {
      setError('This workspace already has a quote. Use Clone workspace if you need another quote under a new lifecycle ref.');
      return;
    }
    setInvoiceChoiceOpen(false);
    setInvoiceSourceQuoteId(null);
    // Open the wizard only — do not insert a documents row until the user clicks Save.
    changePanel({ type: 'create', kind, lifecycleRef: lifecycleRef || project.lifecycle_ref || undefined });
    setVisualDesignerExpanded(true);
    void visualDesignerSurveysService.ensureFirstSurvey(project.id)
      .then(() => refreshSurveys())
      .catch((err) => {
        console.warn('Failed to ensure First Survey for create', err);
      });
  };

  const linkedQuote = latestDocument(quotes);
  // What the customer has agreed to pay: the quote less any deductions on a reissued balance.
  // Every invoice-vs-quote check works from this, so a reissued balance doesn't read as short.
  const agreedGross = Math.max(0, Math.round(
    (Number(linkedQuote?.grossTotal || 0) - totalBalanceDeductions(balanceAdjustments)) * 100,
  ) / 100);
  // The ledger and schedule count every invoice, so hand-raised variations and credits join the
  // total they're measured against; otherwise a variation reads as over-invoicing.
  const ledgerGross = Math.max(0, Math.round((agreedGross + manualAdjustmentTotal(invoices)) * 100) / 100);
  const quoteInvoiceExists = !!(
    linkedQuote
    && invoices.some((invoice) => (
      invoice.lifecycleRef === linkedQuote.lifecycleRef
      && String(invoice.status || '').toLowerCase() !== 'void'
    ))
  );

  const requestCreateInvoice = (sourceQuoteId?: string) => {
    if (!project.customer_id) {
      setError('Assign a customer in Overview before creating documents.');
      return;
    }
    try {
      assertCanIssueCommercialDocument({ project, customer, kind: 'Invoice' });
    } catch (err) {
      setError(err instanceof Error ? err.message : BILLING_DETAILS_REQUIRED_MESSAGE);
      return;
    }
    const quote = (sourceQuoteId && quotes.find((item) => item.id === sourceQuoteId)) || linkedQuote;
    if (quote) {
      setError(null);
      setInvoiceSourceQuoteId(quote.id);
      setInvoiceChoiceOpen(true);
      return;
    }
    startCreate('Invoice');
  };

  const createInvoiceFromQuote = async (quoteId?: string) => {
    try {
      assertCanIssueCommercialDocument({ project, customer, kind: 'Invoice' });
    } catch (err) {
      setError(err instanceof Error ? err.message : BILLING_DETAILS_REQUIRED_MESSAGE);
      return;
    }
    const quote = (quoteId && quotes.find((item) => item.id === quoteId)) || linkedQuote;
    if (!quote) {
      setError('Link or create a quote before issuing a balance invoice.');
      return;
    }
    // Measured the same way as the tab: deductions and hand-raised variations/credits included
    const contractGross = quote.id === linkedQuote?.id ? ledgerGross : Number(quote.grossTotal || 0);
    const final = primarySurvey(surveys, 'final');
    const settled = !!(final && isFinalSurveyOperational({
      completionStatus: final.completionStatus,
      latestClassification: finalDelta?.finalSurveyId === final.id ? finalDelta.classification : undefined,
      customerAcceptedAt: finalDelta?.finalSurveyId === final.id ? finalDelta.customerAcceptedAt : undefined,
      reapprovalTransmissionVersionId: finalDelta?.finalSurveyId === final.id
        ? finalDelta.reapprovalTransmissionVersionId
        : undefined,
    }));
    const earlyScheduleStage = paymentSchedule
      ? presentScheduleStages({
        stages: paymentSchedule.stages,
        grossTotal: contractGross,
        invoices: toScheduleInvoices(invoices),
        finalSettled: settled,
        firstAccepted: isAcceptedOrCashStatus(quote.status),
      }).find((stage) => stage.canIssue)
      : null;
    if (!settled && earlyScheduleStage?.kind !== 'deposit') {
      setError('Final Survey must settle before a balance invoice can be issued.');
      return;
    }
    setCreatingInvoice(true);
    setError(null);
    try {
      const sourceEvent = String(final?.documentVersionId || '').trim()
        || String(finalDelta?.id || '').trim()
        || String(quote.activeVersionId || '').trim()
        || `settled:${Number(quote.grossTotal || 0)}`;
      const scheduleInvoices = toScheduleInvoices(invoices);
      const nextStage = paymentSchedule
        ? presentScheduleStages({
          stages: paymentSchedule.stages,
          grossTotal: contractGross,
          invoices: scheduleInvoices,
          finalSettled: settled,
          firstAccepted: isAcceptedOrCashStatus(quote.status),
        }).find((stage) => stage.canIssue)
        : null;
      const issuingStage = nextStage
        ? paymentSchedule?.stages.find((stage) => stage.id === nextStage.id) || null
        : null;
      if (paymentSchedule && !issuingStage) {
        setError('No Payment Schedule milestone is ready to issue.');
        return;
      }
      const created = issuingStage?.kind === 'deposit'
        ? await documentsService.ensureDepositInvoice(quote.id)
        : await documentsService.issueBalanceInvoice(quote.id, issuingStage
          ? {
            settledContract: contractGross,
            sourceSurveyEvent: `schedule-stage:${issuingStage.id}`,
            amount: nextIssueAmount({
              stage: issuingStage,
              grossTotal: contractGross,
              invoices: scheduleInvoices,
            }),
            role: 'works',
            title: `${issuingStage.label} — ${quote.title}`,
            lineName: issuingStage.label,
          }
          : {
            settledContract: contractGross,
            sourceSurveyEvent: sourceEvent,
          });
      if (!created) {
        setInvoiceChoiceOpen(false);
        setInvoiceSourceQuoteId(null);
        setError('Nothing to invoice — settled contract already matches invoiced totals.');
        return;
      }
      if (created.projectId !== project.id) {
        await documentsService.updateDocument(created.id, { projectId: project.id });
      }
      if (paymentSchedule && issuingStage) {
        await paymentScheduleService.bindIssuedStage({
          schedule: paymentSchedule,
          stageId: issuingStage.id,
          invoiceId: created.id,
          issuedAmount: Number(created.grossTotal || created.totals?.grandTotal || 0),
        });
      }
      setInvoiceChoiceOpen(false);
      setInvoiceSourceQuoteId(null);
      changePanel({ type: 'view', id: created.id });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to issue balance invoice');
    } finally {
      setCreatingInvoice(false);
    }
  };

  const acceptedDepositSource = [linkedQuote, latestDocument(estimates)].find((doc) => (
    doc
    && isAcceptedOrCashStatus(doc.status)
    && doc.hasDeposit
    && Number(doc.depositAmount || 0) > 0
  )) || null;

  const createMissingDepositInvoice = async () => {
    try {
      assertCanIssueCommercialDocument({ project, customer, kind: 'Invoice' });
    } catch (err) {
      setError(err instanceof Error ? err.message : BILLING_DETAILS_REQUIRED_MESSAGE);
      return;
    }
    const source = acceptedDepositSource;
    if (!source) {
      setError('No accepted quote or estimate with a deposit was found.');
      return;
    }
    setCreatingInvoice(true);
    setError(null);
    try {
      const created = await documentsService.ensureDepositInvoice(source.id);
      if (!created) {
        setError('No deposit is set on the accepted quote, so no deposit invoice was created.');
        return;
      }
      if (created.projectId !== project.id) {
        await documentsService.updateDocument(created.id, { projectId: project.id });
      }
      changePanel({ type: 'view', id: created.id });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create deposit invoice');
    } finally {
      setCreatingInvoice(false);
    }
  };

  const allLanes: Array<{
    kind: DocumentKind;
    title: string;
    description: string;
    icon: IconType;
    docs: Document[];
    emptyTitle: string;
    emptyBody: string;
    createLabel: string;
  }> = [
    {
      kind: 'Estimate',
      title: 'Estimate',
      description: 'Optional. Many jobs go straight to a quote.',
      icon: MdOutlineDescription,
      docs: estimates,
      emptyTitle: 'No estimate yet',
      emptyBody: quoteBlocksEstimate
        ? 'This job went straight to a quote. An estimate can be added if the quote is removed.'
        : 'Create an estimate to capture initial costs.',
      createLabel: 'New estimate',
    },
    {
      kind: 'Quotation',
      title: 'Quote',
      description: 'One quote per workspace.',
      icon: MdRequestQuote,
      docs: quotes,
      emptyTitle: 'No quote yet',
      emptyBody: 'Starts First Survey (Quote Details + Visual Designer).',
      createLabel: 'New quote',
    },
    {
      kind: 'Invoice',
      title: 'Invoice',
      description: 'Deposit is frozen on First accept. Issue the balance when Final has settled.',
      icon: MdReceiptLong,
      docs: invoices,
        emptyTitle: 'No invoice yet',
        emptyBody: linkedQuote
          ? 'A deposit invoice appears on First accept. Issue the balance here when Final has settled.'
          : 'Create an invoice when the job is ready to bill.',
        createLabel: 'Issue balance invoice',
    },
  ];
  const lanes = allLanes.filter((lane) => (isQuotesSection ? lane.kind !== 'Invoice' : lane.kind === 'Invoice'));

  const firstSurvey = primarySurvey(surveys, 'first');
  const finalSurvey = primarySurvey(surveys, 'final');
  const firstWorkingVersionId = String(firstSurvey?.documentVersionId || '').trim();
  const anyCommercialVersionTransmitted = Object.values(versionLockById).some((lock) => lock.isTransmitted);
  const firstIsTransmitted = firstSurveyHasBeenTransmitted({
    firstVersionIsTransmitted: !!(
      firstWorkingVersionId
      && versionLockById[firstWorkingVersionId]?.isTransmitted
    ),
    anyCommercialVersionIsTransmitted: anyCommercialVersionTransmitted,
    hasFinalSurvey: !!finalSurvey,
  });
  const firstIsHistorical = firstSurveyIsHistorical(firstIsTransmitted);
  const firstHistoricalReason = firstSurveyHistoricalLockReason(firstIsHistorical);
  const firstSurveySendLocked = firstIsHistorical;
  const latestQuote = latestDocument(quotes);
  const rolledFromAcceptedEstimate = !!latestQuote && isAcceptedOrCashStatus(latestDocument(estimates)?.status);
  const showFinalTab = finalSurveyTabAvailable({
    hasFinalSurvey: !!finalSurvey,
    firstIsTransmitted,
    quoteStatus: latestQuote?.status,
    documentKind: latestQuote?.kind,
    rolledFromAcceptedEstimate,
    finalWorkStarted: finalSurveyWorkPhase(finalSurvey) !== 'not_started',
  });
  const firstTabStatus = firstSurveyStatusChip({
    firstIsTransmitted,
    quoteStatus: (latestQuote || latestDocument(estimates))?.status,
  });
  const finalDeltaForSurvey = finalDelta?.finalSurveyId === finalSurvey?.id ? finalDelta : null;
  const commercialFinalAcceptance = resolveCommercialFinalAcceptance({
    classification: finalDeltaForSurvey?.classification,
    customerAcceptedAt: finalDeltaForSurvey?.customerAcceptedAt,
    decidedAt: finalDeltaForSurvey?.decidedAt,
    workPhase: finalSurveyWorkPhase(finalSurvey),
  });
  const finalTabStatus = finalSurveyStatusChip({
    workPhase: finalSurveyWorkPhase(finalSurvey),
    customerAcceptedAt: finalDeltaForSurvey?.customerAcceptedAt,
    reapprovalTransmissionVersionId: finalDeltaForSurvey?.reapprovalTransmissionVersionId,
    needsReapproval: !!(finalDelta && isUnresolvedPendingReviewDelta(finalDelta)),
    classification: finalDeltaForSurvey?.classification,
  });
  const finalSettled = !!(finalSurvey && isFinalSurveyOperational({
    completionStatus: finalSurvey.completionStatus,
    latestClassification: finalDelta?.finalSurveyId === finalSurvey.id
      ? finalDelta.classification
      : undefined,
    customerAcceptedAt: finalDelta?.finalSurveyId === finalSurvey.id
      ? finalDelta.customerAcceptedAt
      : undefined,
    reapprovalTransmissionVersionId: finalDelta?.finalSurveyId === finalSurvey.id
      ? finalDelta.reapprovalTransmissionVersionId
      : undefined,
  }));
  const invoiceLedger = computeInvoiceDelta({
    settledContract: ledgerGross,
    invoices: invoices.map((invoice) => ({
      id: invoice.id,
      role: invoice.invoiceRole,
      status: invoice.status,
      grossTotal: invoice.grossTotal,
      depositAmount: invoice.depositAmount,
    })),
  });
  const scheduleInvoices = useMemo(() => toScheduleInvoices(invoices), [invoices]);
  const finalBalanceIssued = useMemo(() => isFinalBalanceIssued({
    invoices: invoices.map((invoice) => ({
      id: invoice.id,
      role: invoice.invoiceRole,
      status: invoice.status,
    })),
    scheduleStages: paymentSchedule?.stages || null,
  }), [invoices, paymentSchedule]);
  const presentedScheduleStages = useMemo(
    () => (paymentSchedule
      ? presentScheduleStages({
        stages: paymentSchedule.stages,
        grossTotal: ledgerGross,
        invoices: scheduleInvoices,
        finalSettled,
        firstAccepted: isAcceptedOrCashStatus(linkedQuote?.status)
          || isAcceptedOrCashStatus(latestDocument(estimates)?.status)
          || firstIsHistorical,
      })
      : []),
    [paymentSchedule, linkedQuote, ledgerGross, scheduleInvoices, finalSettled, estimates, firstIsHistorical],
  );

  useEffect(() => {
    if (!loadedOnce) return undefined;
    let alive = true;
    void (async () => {
      try {
        if (!isQuotesSection) {
          await paymentScheduleService.ensureFromQuote({
            projectId: project.id,
            quote: linkedQuote,
            invoices: scheduleInvoices,
            firstSurveyTransmitted: firstIsTransmitted,
          });
        }
        const refreshed = await paymentScheduleService.refresh({
          projectId: project.id,
          grossTotal: ledgerGross,
          invoices: scheduleInvoices,
          finalSettled,
          firstAccepted: isAcceptedOrCashStatus(linkedQuote?.status)
            || isAcceptedOrCashStatus(latestDocument(estimates)?.status)
            || firstIsHistorical,
        });
        if (alive) setPaymentSchedule(refreshed);
      } catch (err) {
        console.warn('Failed to load Payment Schedule', err);
      }
    })();
    return () => { alive = false; };
  }, [
    loadedOnce,
    isQuotesSection,
    project.id,
    linkedQuote,
    ledgerGross,
    scheduleInvoices,
    firstIsTransmitted,
    finalSettled,
    estimates,
    firstIsHistorical,
    scheduleTick,
  ]);

  const standaloneInvoicing = quotes.length === 0 && estimates.length === 0;
  const invoiceCreateButton = standaloneInvoicing
    ? { label: 'Create Invoice' as const, enabled: true, postSettlement: false }
    : invoiceTabCreateButton({ finalBalanceIssued });

  const startStandaloneInvoice = (role: 'deposit' | 'works') => {
    if (!standaloneInvoicing) return;
    if (!project.customer_id) {
      setError('Assign a customer in Overview before creating documents.');
      return;
    }
    try {
      assertCanIssueCommercialDocument({ project, customer, kind: 'Invoice' });
    } catch (err) {
      setError(err instanceof Error ? err.message : BILLING_DETAILS_REQUIRED_MESSAGE);
      return;
    }
    const name = role === 'deposit' ? 'Deposit' : 'Works';
    setError(null);
    setStandaloneChoiceOpen(false);
    changePanel({
      type: 'create',
      kind: 'Invoice',
      lifecycleRef: project.lifecycle_ref || undefined,
      invoiceRole: role,
      preferredTitle: project.project_name ? `${name} — ${project.project_name}` : name,
      seedLines: [{
        lineKind: 'other',
        additionalType: 'other',
        name,
        qty: 1,
        unit: 'item',
        unitPrice: 0,
        vatInclusive: true,
        sortOrder: 0,
      }],
    });
  };

  const startPostSettlementInvoice = (role: 'variation' | 'credit') => {
    if (!invoiceCreateButton.postSettlement) return;
    if (!project.customer_id) {
      setError('Assign a customer in Overview before creating documents.');
      return;
    }
    try {
      assertCanIssueCommercialDocument({ project, customer, kind: 'Invoice' });
    } catch (err) {
      setError(err instanceof Error ? err.message : BILLING_DETAILS_REQUIRED_MESSAGE);
      return;
    }
    const source = linkedQuote
      || invoices.find((invoice) => normalizeInvoiceRole(invoice.invoiceRole) === 'works')
      || invoices[0];
    if (!source) return;
    const leftover = role === 'credit'
      ? (invoiceLedger.delta < 0 ? Math.abs(invoiceLedger.delta) : 0)
      : (invoiceLedger.delta > 0 ? invoiceLedger.delta : 0);
    setError(null);
    setAdjustmentChoiceOpen(false);
    changePanel({
      type: 'create',
      kind: 'Invoice',
      lifecycleRef: source.lifecycleRef || project.lifecycle_ref || undefined,
      invoiceRole: role,
      preferredTitle: `${role === 'credit' ? 'Credit' : 'Variation'} — ${source.title}`,
      seedLines: [{
        lineKind: 'other',
        additionalType: 'other',
        name: role === 'credit' ? 'Credit' : 'Variation',
        qty: 1,
        unit: 'item',
        unitPrice: leftover,
        vatInclusive: true,
        sortOrder: 0,
      }],
    });
  };

  useLayoutEffect(() => {
    setQuotePane(showFinalTab ? 'final' : 'first');
  }, [showFinalTab, project.id]);
  const defaultReference = useMemo(() => {
    const name = String(customer?.name || '').trim();
    if (!name) return '';
    const parts = name.split(/\s+/).filter(Boolean);
    return (parts.slice(1).join(' ') || parts[0] || '').trim().toUpperCase();
  }, [customer?.name]);

  const sendQuote = (doc: Document) => {
    requestLeave(() => {
      void (async () => {
        try {
          assertCanIssueCommercialDocument({ project, customer, kind: doc.kind });
        } catch (err) {
          setError(err instanceof Error ? err.message : BILLING_DETAILS_REQUIRED_MESSAGE);
          return;
        }
        const email = String(commercialIdentity.email || '').trim();
        if (!email) {
          setError(commercialIdentity.usesBilling
            ? BILLING_DETAILS_REQUIRED_MESSAGE
            : 'Customer email is missing. Add it in Overview before sending.');
          return;
        }

        const first = primarySurvey(surveys, 'first');
        const firstWorkingId = String(first?.documentVersionId || '').trim();
        if (!firstWorkingId) {
          setError('First Survey has no working quote record to send. Open First Survey and save Quote Details first.');
          return;
        }

        if (firstSurveyIsHistorical(firstIsTransmitted)) {
          setError(
            firstSurveyHistoricalLockReason(true)
            || 'First Survey is permanently immutable after transmission. Update Final Survey instead.',
          );
          return;
        }

        const status = String(doc.status || '').trim().toLowerCase();
        const terminal = status === 'accepted' || status === 'invoiced' || status === 'paid' || status === 'cash';
        const firstLock = versionLockById[firstWorkingId];
        if (firstLock?.isTransmitted) {
          setError('First Survey has already been transmitted and is permanently immutable.');
          return;
        }

        setSendingQuote(true);
        try {
          const ready = await ensureSignedIn();
          if (!ready) {
            setError(GRAPH_SIGNIN_REQUIRED_TO_SEND);
            return;
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : GRAPH_SIGNIN_REQUIRED_TO_SEND);
          return;
        } finally {
          setSendingQuote(false);
        }

        setConfirmDialog({
          title: 'Send First Survey',
          body: `Send First Survey ${doc.lifecycleRef} to ${email}?`,
          confirmLabel: 'Send',
          onConfirm: () => { void applySendQuote(doc, email, firstWorkingId, terminal); },
        });
      })();
    });
  };

  const applySendQuote = async (
    doc: Document,
    email: string,
    firstWorkingId: string,
    terminal: boolean,
  ) => {
    setSendingQuote(true);
    setError(null);
    try {
      const ready = await ensureSignedIn();
      if (!ready) {
        setError(GRAPH_SIGNIN_REQUIRED_TO_SEND);
        return;
      }
      const sendResult = await sendDocumentToCustomer(doc.id, {
        versionId: firstWorkingId,
        preserveDocumentStatus: terminal,
        surveyArtefact: 'first',
      });
      await load();
      await refreshSurveys();
      await refreshSurveyVersionLocks();
      setLockRefreshKey((value) => value + 1);
      setNoticeDialog({
        title: 'First Survey sent',
        body: `Sent First Survey ${doc.lifecycleRef} to ${email}.`,
        url: sendResult.shareUrl,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send this quote');
    } finally {
      setSendingQuote(false);
    }
  };

  const visualSurveyAttachable = !!(
    vdSurvey
    && (!vdSurvey.documentVersionId || vdSurvey.documentVersionId === vdVersionLock?.id)
    && (!vdVersionLock?.visualSurveyId || vdVersionLock.visualSurveyId === vdSurvey.id)
  );
  const visualDesignerPairingLocked = !!(
    panel.type === 'edit'
    && vdVersionLock?.isTransmitted
    && !vdVersionLock.overrideUnlocked
  );

  const refreshVersionSummaries = useCallback(async () => {
    const ids = [...estimates, ...quotes, ...candidateQuotes].map((doc) => doc.id);
    if (ids.length === 0) return;
    try {
      setVersionById(await documentsService.getActiveVersionSummaries(ids));
    } catch (err) {
      console.warn('Failed to refresh document versions', err);
    }
  }, [estimates, quotes, candidateQuotes]);

  const refreshSurveyVersionLocks = useCallback(async () => {
    const quote = latestDocument(quotes) || latestDocument(estimates);
    if (!quote) {
      setVersionLockById({});
      return;
    }
    try {
      const versions = await documentsService.listVersions(quote.id);
      const next: Record<string, { isTransmitted: boolean; overrideUnlocked: boolean }> = {};
      versions.forEach((row) => {
        next[String(row.id)] = {
          isTransmitted: !!row.isTransmitted,
          overrideUnlocked: !!row.overrideUnlocked,
        };
      });
      setVersionLockById(next);
    } catch (err) {
      console.warn('Failed to refresh survey version locks', err);
    }
  }, [quotes, estimates]);

  useEffect(() => {
    void refreshSurveyVersionLocks();
  }, [refreshSurveyVersionLocks, lockRefreshKey, surveys]);

  const notifyVersionsChanged = useCallback(() => {
    void refreshVersionSummaries();
    void refreshSurveyVersionLocks();
    setLockRefreshKey((value) => value + 1);
  }, [refreshVersionSummaries, refreshSurveyVersionLocks]);

  const visualDesignerBridge = {
    visualSurveyId: vdSurveyId || null,
    visualSurveyLabel: vdSurvey?.label || null,
    visualDesignerLines: visualSurveyAttachable ? vdLines : [],
    visualSurveyAttachable,
    visualSurveys: surveys,
    visualDesignerPairingLocked,
    onVisualSurveyChange: setVdSurveyId,
    onOpenVisualDesigner: openVisualDesigner,
    onVersionLockChange: setVdVersionLock,
    onVersionsChanged: notifyVersionsChanged,
  };

  const selectedIsQuoteLike = (panel.type === 'view' || panel.type === 'edit')
    && (
      quotes.some((doc) => doc.id === panel.id)
      || estimates.some((doc) => doc.id === panel.id)
    );
  const quoteWorkspaceOpen = isQuotesSection && selectedIsQuoteLike;

  const refreshSurveys = useCallback(async () => {
    try {
      const list = await visualDesignerSurveysService.listByProject(project.id);
      setSurveys(list);
    } catch (err) {
      console.warn('Failed to refresh surveys', err);
    }
  }, [project.id]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const changed = await visualDesignerSurveysService.repairTransmittedSurveyLifecycle(project.id);
        if (cancelled || !changed) return;
        await refreshSurveys();
        await refreshSurveyVersionLocks();
        setLockRefreshKey((value) => value + 1);
      } catch (err) {
        console.warn('Transmitted survey lifecycle repair skipped', err);
      }
    })();
    return () => { cancelled = true; };
  }, [project.id, refreshSurveys, refreshSurveyVersionLocks]);

  const refreshFinalDelta = useCallback(async (finalSurveyId?: string | null) => {
    const id = String(finalSurveyId || '').trim();
    if (!id) {
      setFinalDelta(null);
      return;
    }
    try {
      const latest = await visualDesignerSurveysService.getLatestFinalSurveyDelta(id);
      setFinalDelta(latest);
    } catch (err) {
      console.warn('Failed to load Final Survey delta', err);
      setFinalDelta(null);
    }
  }, []);

  const finalSurveyIdRef = useRef<string | null>(null);
  finalSurveyIdRef.current = surveys.find((survey) => survey.kind === 'final')?.id || null;

  useEffect(() => {
    const finalId = surveys.find((s) => s.kind === 'final')?.id || null;
    void refreshFinalDelta(finalId);
  }, [surveys, refreshFinalDelta]);

  useEffect(() => {
    const projectId = String(project.id || '').trim();
    if (!projectId) return;
    let reloadTimer: ReturnType<typeof setTimeout> | null = null;
    const channel = directSupabase
      .channel(`workspace-lifecycle-${projectId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'documents',
          filter: `project_id=eq.${projectId}`,
        },
        () => {
          if (reloadTimer) clearTimeout(reloadTimer);
          reloadTimer = setTimeout(() => {
            reloadTimer = null;
            void load();
          }, 400);
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'final_survey_deltas',
          filter: `project_id=eq.${projectId}`,
        },
        () => { void refreshFinalDelta(finalSurveyIdRef.current); },
      )
      .subscribe();
    return () => {
      if (reloadTimer) clearTimeout(reloadTimer);
      void directSupabase.removeChannel(channel);
    };
  }, [project.id, load, refreshFinalDelta]);

  // Repair virgin Final if it still shares First's transmitted version (or was wrongly locked).
  useEffect(() => {
    if (quotePane !== 'final') return;
    const final = surveys.find((s) => s.kind === 'final');
    const first = surveys.find((s) => s.kind === 'first');
    if (!final || final.completionStatus === 'complete') return;
    if (finalDelta && isUnresolvedPendingReviewDelta(finalDelta)) return;
    const finalVid = String(final.documentVersionId || '');
    const firstVid = String(first?.documentVersionId || '');
    const lock = finalVid ? versionLockById[finalVid] : null;
    const needsRepair = !finalVid
      || (!!firstVid && finalVid === firstVid)
      || !!(lock?.isTransmitted && !lock.overrideUnlocked);
    if (!needsRepair) return;
    let cancelled = false;
    void (async () => {
      try {
        await visualDesignerSurveysService.createFinalSurveyFromFirst(project.id);
        if (cancelled) return;
        await refreshSurveys();
        await refreshSurveyVersionLocks();
        setLockRefreshKey((value) => value + 1);
      } catch (err) {
        console.warn('Final Survey writable repair skipped', err);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotePane, project.id, surveys, versionLockById, finalDelta]);

  const emptyFinalDesignsCheckedRef = useRef<string | null>(null);
  useEffect(() => {
    if (quotePane !== 'final') return;
    const final = surveys.find((s) => s.kind === 'final' && !s.optionId);
    if (!final?.documentVersionId || final.completionStatus === 'complete') return;
    if (emptyFinalDesignsCheckedRef.current === final.id) return;
    emptyFinalDesignsCheckedRef.current = final.id;
    let cancelled = false;
    void (async () => {
      try {
        const copied = await visualDesignerSurveysService.copyFirstDesignsIntoEmptyFinal(project.id);
        if (!copied || cancelled) return;
        await refreshSurveys();
        setLockRefreshKey((value) => value + 1);
      } catch (err) {
        console.warn('Copying First Survey designs into empty Final skipped', err);
      }
    })();
    return () => { cancelled = true; };
  }, [quotePane, project.id, surveys, refreshSurveys]);

  const markFinalComplete = () => {
    if (!finalSurvey) return;
    setConfirmDialog({
      title: 'Mark Final Survey complete',
      body: 'This freezes the Final Survey and stores the review of changes against the original First transmission. Closing Review Changes later does not undo completion — Final stays complete until you decide Yes/No or Reopen.',
      confirmLabel: 'Mark complete',
      confirmColor: 'success',
      onConfirm: () => { void applyMarkFinalComplete(); },
    });
  };

  const applyMarkFinalComplete = async () => {
    if (!finalSurvey) return;
    setSurveyActionBusy(true);
    setError(null);
    try {
      const result = await visualDesignerSurveysService.markFinalSurveyComplete(finalSurvey.id);
      await refreshSurveys();
      setFinalDelta(result.delta);
      setReviewDeltaOpen(true);
      await load();
      setLockRefreshKey((value) => value + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark Final Survey complete');
    } finally {
      setSurveyActionBusy(false);
    }
  };

  const openFinalReview = async () => {
    if (!finalSurvey) return;
    setSurveyActionBusy(true);
    setError(null);
    try {
      let latest = finalDelta;
      if (!latest || latest.finalSurveyId !== finalSurvey.id) {
        latest = await visualDesignerSurveysService.getLatestFinalSurveyDelta(finalSurvey.id);
        setFinalDelta(latest);
      }
      if (!latest) {
        setError('No Final Survey review record found. Mark Final Survey Complete first.');
        return;
      }
      setReviewDeltaOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open Review Changes');
    } finally {
      setSurveyActionBusy(false);
    }
  };

  const decideFinalReapproval = async (requiresCustomerReapproval: boolean) => {
    if (!finalDelta?.id) return;
    setReapprovalBusy(true);
    setError(null);
    try {
      if (requiresCustomerReapproval) {
        if (!finalSurvey) {
          setError('Final Survey is missing. Refresh and try again.');
          return;
        }
        const ready = await ensureSignedIn();
        if (!ready) {
          setError(GRAPH_SIGNIN_REQUIRED_TO_SEND);
          return;
        }
      }
      const updated = await visualDesignerSurveysService.decideFinalSurveyReapproval(
        finalDelta.id,
        requiresCustomerReapproval,
      );
      setFinalDelta(updated);
      if (requiresCustomerReapproval && finalSurvey) {
        const result = await visualDesignerSurveysService.transmitFinalSurveyForApproval(finalSurvey.id);
        setFinalDelta(result.delta);
        setReviewDeltaOpen(false);
        await refreshSurveys();
        await load();
        setLockRefreshKey((value) => value + 1);
        setNoticeDialog({
          title: 'Final Survey transmitted',
          body: 'Final Survey transmitted for approval.',
          url: result.shareUrl,
        });
        return;
      }
      setReviewDeltaOpen(false);
      await refreshSurveys();
    } catch (err) {
      setError(err instanceof Error ? err.message : requiresCustomerReapproval
        ? 'Failed to send Final Survey for approval'
        : 'Failed to save reapproval decision');
    } finally {
      setReapprovalBusy(false);
    }
  };

  const transmitFinalForApproval = () => {
    if (!finalSurvey) return;
    void (async () => {
      setSurveyActionBusy(true);
      try {
        const ready = await ensureSignedIn();
        if (!ready) {
          setError(GRAPH_SIGNIN_REQUIRED_TO_SEND);
          return;
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : GRAPH_SIGNIN_REQUIRED_TO_SEND);
        return;
      } finally {
        setSurveyActionBusy(false);
      }
      const replacingInFlight = isInFlightFinalReapproval(
        finalDelta?.finalSurveyId === finalSurvey.id ? finalDelta : null,
      );
      setConfirmDialog({
        title: replacingInFlight
          ? 'Replace the outstanding Final approval?'
          : 'Transmit Final Survey for approval',
        alert: replacingInFlight
          ? 'The customer already has an approval request. Retransmitting nullifies their current link and sends a new one.'
          : null,
        body: replacingInFlight
          ? 'This creates a new immutable Final transmission and emails a new public URL. The previous link can no longer be accepted. Final becomes operational only after the customer accepts the new request.'
          : 'This creates an immutable Final transmission, emails the customer a new public URL, and shows Review Changes on that link. Final becomes operational only after the customer accepts.',
        confirmLabel: replacingInFlight ? 'Withdraw previous and transmit' : 'Transmit',
        confirmColor: 'warning',
        onConfirm: () => { void applyTransmitFinalForApproval(); },
      });
    })();
  };

  const applyTransmitFinalForApproval = async () => {
    if (!finalSurvey) return;
    setSurveyActionBusy(true);
    setError(null);
    try {
      const ready = await ensureSignedIn();
      if (!ready) {
        setError(GRAPH_SIGNIN_REQUIRED_TO_SEND);
        return;
      }
      const result = await visualDesignerSurveysService.transmitFinalSurveyForApproval(finalSurvey.id);
      setFinalDelta(result.delta);
      await refreshSurveys();
      await load();
      setLockRefreshKey((value) => value + 1);
      setNoticeDialog({
        title: 'Final Survey transmitted',
        body: 'Final Survey transmitted for approval.',
        url: result.shareUrl,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to transmit Final Survey for approval');
    } finally {
      setSurveyActionBusy(false);
    }
  };

  const createFinalSurvey = () => {
    setConfirmDialog({
      title: 'Create Final Survey',
      body: 'Normally Final is created when the quote is Accepted or Cash. This clones First Survey (Quote Details + Visual Designer) into an independent Final Survey.',
      confirmLabel: 'Create Final Survey',
      confirmColor: 'success',
      onConfirm: () => { void applyCreateFinalSurvey(); },
    });
  };

  const applyCreateFinalSurvey = async () => {
    setSurveyActionBusy(true);
    setError(null);
    try {
      await visualDesignerSurveysService.createFinalSurveyFromFirst(project.id);
      await refreshSurveys();
      await load();
      setLockRefreshKey((value) => value + 1);
      setQuotePane('final');
      const quoteId = (panel.type === 'view' || panel.type === 'edit' ? panel.id : null)
        || quotes[0]?.id
        || estimates[0]?.id;
      if (quoteId) setPanel({ type: 'view', id: quoteId });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create Final Survey');
    } finally {
      setSurveyActionBusy(false);
    }
  };

  const reopenFinalSurvey = () => {
    if (!finalSurvey) return;
    if (finalBalanceIssued) {
      setError(FINAL_SURVEY_HISTORICAL_AFTER_BALANCE);
      return;
    }
    const copy = finalReopenConfirmCopy(
      isInFlightFinalReapproval(finalDelta?.finalSurveyId === finalSurvey.id ? finalDelta : null),
    );
    setConfirmDialog({
      title: copy.title,
      alert: copy.alert,
      body: copy.body,
      confirmLabel: copy.confirmLabel,
      confirmColor: copy.confirmColor,
      onConfirm: () => { void applyReopenFinalSurvey(); },
    });
  };

  const applyReopenFinalSurvey = async () => {
    if (!finalSurvey) return;
    setSurveyActionBusy(true);
    setError(null);
    try {
      await visualDesignerSurveysService.reopenFinalSurvey(finalSurvey.id);
      await refreshSurveys();
      await refreshSurveyVersionLocks();
      await load();
      setLockRefreshKey((value) => value + 1);
      setQuotePane('final');
      await refreshFinalDelta(finalSurvey.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reopen Final Survey');
    } finally {
      setSurveyActionBusy(false);
    }
  };

  const clearSurveySheet = async (survey: VisualDesignerSurvey) => {
    setSurveyActionBusy(true);
    setError(null);
    try {
      const next = await visualDesignerSurveysService.resetSurveySheet(survey.id);
      await refreshSurveys();
      await load();
      setLockRefreshKey((value) => value + 1);
      // Final delete returns null — stay on Final tab so locked/accepted states are visible.
      if (next) {
        setQuotePane(next.kind === 'final' ? 'final' : 'first');
      } else if (survey.kind === 'final') {
        setQuotePane('final');
      } else {
        setQuotePane('first');
      }
      if (panel.type === 'edit' || panel.type === 'view') {
        const quoteId = panel.id || quotes[0]?.id;
        if (quoteId) setPanel({ type: 'view', id: quoteId });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear survey');
      throw err;
    } finally {
      setSurveyActionBusy(false);
    }
  };

  const surveyVisualBridge = (survey: VisualDesignerSurvey | null | undefined) => ({
    visualSurveyId: survey?.id || null,
    visualSurveyLabel: survey?.label || null,
    // VD items live on the survey; Quote Details save must not wipe version lines with [].
    visualDesignerLines: [] as DocumentLineInput[],
    visualSurveyAttachable: !!survey,
    visualSurveys: surveys,
    visualDesignerPairingLocked,
    onVisualSurveyChange: setVdSurveyId,
    onOpenVisualDesigner: openVisualDesigner,
    onVersionLockChange: setVdVersionLock,
    onVersionsChanged: notifyVersionsChanged,
  });

  const renderSurveyWorkspace = (kind: QuotePane) => {
    const survey = kind === 'final' ? finalSurvey : firstSurvey;
    const commercialDoc = latestDocument(quotes) || latestDocument(estimates);
    const quoteDoc = latestDocument(quotes);
    const firstStatus = firstSurveyStatusChip({
      firstIsTransmitted,
      quoteStatus: commercialDoc?.status,
    });
    const finalTabOpen = finalSurveyTabAvailable({
      hasFinalSurvey: !!finalSurvey,
      firstIsTransmitted,
      quoteStatus: quoteDoc?.status,
      documentKind: quoteDoc?.kind,
      rolledFromAcceptedEstimate,
      finalWorkStarted: finalSurveyWorkPhase(finalSurvey) !== 'not_started',
    });

    if (kind === 'final' && !finalTabOpen) {
      return (
        <Alert color="neutral">
          Final Survey becomes available when First Survey is accepted or Cash, or after an accepted estimate rolls to a draft quote.
        </Alert>
      );
    }

    if (kind === 'final' && !survey) {
      return (
        <Stack gap={1.5}>
          <Alert color="primary">
            Final Survey is created when First Survey is accepted or Cash, or when an accepted estimate rolls to a draft quote. If provision failed, create it manually below.
          </Alert>
          <Button
            size="sm"
            color="success"
            loading={surveyActionBusy}
            onClick={() => { void createFinalSurvey(); }}
          >
            Create Final Survey
          </Button>
        </Stack>
      );
    }
    if (!survey) {
      return <Alert color="neutral">First Survey is being prepared for this workspace…</Alert>;
    }

    const versionId = survey.documentVersionId || null;
    const versionLock = versionId ? versionLockById[versionId] : null;
    const surveyLabel = kind === 'final' ? 'Final Survey' : 'First Survey';
    const workPhase = kind === 'final' ? finalSurveyWorkPhase(survey) : null;
    const finalOpState = kind === 'final'
      ? finalSurveyOperationalState({
        completionStatus: survey.completionStatus,
        latestClassification: finalDelta?.finalSurveyId === survey.id
          ? finalDelta.classification
          : undefined,
        customerAcceptedAt: finalDelta?.finalSurveyId === survey.id
          ? finalDelta.customerAcceptedAt
          : undefined,
        reapprovalTransmissionVersionId: finalDelta?.finalSurveyId === survey.id
          ? finalDelta.reapprovalTransmissionVersionId
          : undefined,
      })
      : null;
    // First: permanently historical after transmit. Final: freely editable until Mark Complete.
    const firstHistorical = kind === 'first' && firstIsHistorical;
    const finalIncomplete = kind === 'final' && workPhase !== 'complete' && finalOpState !== 'pending_review';
    const finalComplete = kind === 'final' && (workPhase === 'complete' || finalOpState === 'pending_review');
    const versionEditLocked = firstHistorical
      ? true
      : finalIncomplete
        ? false
        : finalComplete
          ? true
          : !!(versionLock?.isTransmitted && !versionLock.overrideUnlocked);
    const editDisabledReason = firstHistorical
      ? (firstHistoricalReason || 'First Survey is historical.')
      : finalBalanceIssued && kind === 'final'
        ? FINAL_SURVEY_HISTORICAL_AFTER_BALANCE
        : finalComplete
          ? 'Reopen Final Survey before editing.'
          : versionEditLocked
            ? 'This survey is locked.'
            : null;
    const showEditor = panel.type === 'edit' && !versionEditLocked;

    return (
      <Stack gap={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={1}>
          <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
            <Chip size="sm" variant="soft" color={kind === 'first' ? 'primary' : 'success'}>
              {surveyLabel}
            </Chip>
            {kind === 'first' && (
              <Chip size="sm" variant="soft" color={firstStatus.color}>
                {firstStatus.label}
              </Chip>
            )}
            {firstHistorical && (
              <Chip size="sm" variant="soft" color="neutral">HISTORICAL</Chip>
            )}
            {kind === 'final' && workPhase === 'not_started' && (
              <Chip size="sm" variant="outlined" color="neutral">Not issued</Chip>
            )}
            {kind === 'final' && workPhase === 'in_progress' && (
              <Chip size="sm" variant="outlined" color="primary">In progress</Chip>
            )}
            {kind === 'final' && workPhase === 'complete' && (
              <Chip size="sm" variant="soft" color="neutral">Complete</Chip>
            )}
            {kind === 'final' && finalBalanceIssued && (
              <Chip size="sm" variant="soft" color="neutral">HISTORICAL</Chip>
            )}
            {kind === 'final' && finalOpState === 'pending_review' && (
              <Chip size="sm" variant="soft" color="warning">Changes need reviewing</Chip>
            )}
            {kind === 'final' && finalOpState === 'awaiting_customer_reapproval' && (
              <Chip size="sm" variant="soft" color="warning">Customer reapproval required</Chip>
            )}
            {kind === 'final' && finalOpState === 'awaiting_customer_acceptance' && (
              <Chip size="sm" variant="soft" color="warning">Awaiting customer acceptance</Chip>
            )}
            {kind === 'final' && workPhase === 'not_started' && (
              <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                Cloned from First transmission. Edit freely — work phase becomes In progress on first save.
              </Typography>
            )}
            {kind === 'final' && workPhase === 'in_progress' && (
              <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                Save stores progress on this Final Survey. Mark complete when ready to review changes.
              </Typography>
            )}
            {kind === 'final' && finalOpState === 'pending_review' && !finalBalanceIssued && (
              <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                Complete and frozen. Closing Review Changes does not undo this — decide Yes/No, or Reopen Final Survey to start a new working cycle.
              </Typography>
            )}
            {kind === 'final' && finalBalanceIssued && (
              <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                {FINAL_SURVEY_HISTORICAL_AFTER_BALANCE}
              </Typography>
            )}
            {kind === 'final' && finalOpState === 'awaiting_customer_reapproval' && (
              <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                Customer reapproval required. Transmit for Approval creates the immutable customer record; Final is not operational until they accept.
              </Typography>
            )}
          </Stack>
          <Stack direction="row" gap={1} flexWrap="wrap">
            {kind === 'final' && finalIncomplete && (
              <Button size="sm" color="success" loading={surveyActionBusy} onClick={() => { void markFinalComplete(); }}>
                Mark Final Survey Complete
              </Button>
            )}
            {kind === 'final' && finalOpState === 'pending_review' && (
              <Button size="sm" color="warning" loading={surveyActionBusy} onClick={() => { void openFinalReview(); }}>
                Review Changes
              </Button>
            )}
            {kind === 'final' && finalComplete && !finalBalanceIssued && (
              <Button size="sm" variant="outlined" color="warning" loading={surveyActionBusy} onClick={() => { void reopenFinalSurvey(); }}>
                Reopen Final Survey
              </Button>
            )}
            {kind === 'final' && finalOpState === 'awaiting_customer_reapproval' && (
              <>
                <Button size="sm" variant="outlined" loading={surveyActionBusy} onClick={() => { void openFinalReview(); }}>
                  Review Changes
                </Button>
                <Button size="sm" color="warning" loading={surveyActionBusy} onClick={() => { void transmitFinalForApproval(); }}>
                  Transmit for Approval
                </Button>
              </>
            )}
            {kind === 'final' && finalOpState === 'awaiting_customer_acceptance' && (
              <>
                <Button size="sm" variant="outlined" loading={surveyActionBusy} onClick={() => { void openFinalReview(); }}>
                  Review Changes
                </Button>
                <Button size="sm" variant="soft" color="warning" loading={surveyActionBusy} onClick={() => { void transmitFinalForApproval(); }}>
                  Retransmit for Approval
                </Button>
              </>
            )}
          </Stack>
        </Stack>

        {kind === 'final' && finalOpState === 'awaiting_customer_acceptance' && (
          <Alert color="warning">
            <Typography level="title-sm">Customer approval is outstanding</Typography>
            <Typography level="body-sm">{FINAL_IN_FLIGHT_TAB_ALERT}</Typography>
          </Alert>
        )}

        {commercialDoc && versionId && (
          <SurveyTransmissionMismatchAlert
            documentId={commercialDoc.id}
            artefact={kind}
            workingVersionId={versionId}
            workingVersionNumber={versionById[commercialDoc.id]?.versionId === versionId
              ? versionById[commercialDoc.id]?.versionNumber
              : null}
            isTransmitted={versionLock?.isTransmitted}
            overrideUnlocked={versionLock?.overrideUnlocked}
            firstWorkingVersionId={firstSurvey?.documentVersionId || null}
            finalWorkingVersionId={finalSurvey?.documentVersionId || null}
            finalSurveyId={finalSurvey?.id || null}
            finalReapprovalTransmissionVersionId={finalDelta?.reapprovalTransmissionVersionId || null}
            refreshKey={`${lockRefreshKey}-${sendingQuote}-${finalDelta?.reapprovalTransmissionVersionId || ''}`}
          />
        )}

        <Card variant="outlined" sx={{ width: '100%' }}>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <AccordionGroup>
              <Accordion
                expanded={quoteDetailsExpanded}
                onChange={(_, expanded) => setQuoteDetailsExpanded(expanded)}
              >
                <AccordionSummary sx={{ px: 2, py: 1.25 }}>
                  <Typography level="title-sm">Quote Details</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pb: 2 }}>
            {showEditor ? (
              <EstimateQuoteWizard
                key={`edit-${panel.type === 'edit' ? panel.id : survey.id}-${survey.id}-${versionId || 'none'}`}
                embedded
                isEditMode
                documentId={panel.type === 'edit' ? panel.id : undefined}
                projectId={project.id}
                workspaceProject={project}
                customerId={project.customer_id}
                preferredVersionId={versionId}
                lockCustomer
                lockKind
                versionLayout="vertical"
                hideVersions
                omitLockControls
                lockLifecycleRef
                lockRefreshKey={lockRefreshKey}
                leaveGuardRef={wizardLeaveRef}
                onClose={() => {
                  if (panel.type === 'edit') setPanel({ type: 'view', id: panel.id });
                }}
                onDocumentReady={handleDocumentReady}
                {...surveyVisualBridge(survey)}
              />
            ) : (
              <DocumentDetailPage
                key={`view-${panel.type === 'view' || panel.type === 'edit' ? panel.id : survey.id}-${survey.id}-${versionId || 'none'}`}
                documentId={panel.type === 'view' || panel.type === 'edit' ? panel.id : ''}
                preferredVersionId={versionId}
                embedded
                onClose={() => setPanel({ type: 'idle' })}
                surveyDelete={{
                  surveyLabel: kind === 'final' ? 'Final Survey' : 'First Survey',
                  disabledReason: surveySheetDeleteBlockedReason({
                    kind,
                    firstHasBeenTransmitted: firstIsHistorical,
                  }),
                  onDelete: async () => {
                    if (surveyActionBusy) return;
                    await clearSurveySheet(survey);
                  },
                }}
                editDisabledReason={editDisabledReason}
                hideCustomerSend={kind === 'final' || firstHistorical}
                onCustomerSend={kind === 'first' && commercialDoc ? () => sendQuote(commercialDoc) : undefined}
                customerSendBusy={sendingQuote}
                onDeleted={() => {
                  setQuotePane(kind);
                  void load();
                  void refreshSurveys();
                }}
                onEdit={(documentId) => {
                  if (editDisabledReason) {
                    setNoticeDialog({ title: 'Cannot edit', body: editDisabledReason });
                    return;
                  }
                  setQuotePane(kind);
                  setPanel({ type: 'edit', id: documentId });
                }}
                offlinePreviewFinalSurveyId={kind === 'final' ? survey.id : null}
                exportPdfSignal={(panel.type === 'view' || panel.type === 'edit') && panel.id === exportPdfDocId ? exportPdfSignal : 0}
                onExportPdfTriggered={handleExportPdfTriggered}
                onDocumentChanged={(documentId) => {
                  setPanel({ type: 'view', id: documentId });
                  void load();
                  void refreshSurveys();
                }}
              />
            )}
                </AccordionDetails>
              </Accordion>
            </AccordionGroup>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ width: '100%' }}>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <AccordionGroup>
              <Accordion
                expanded={visualDesignerExpanded}
                onChange={(_, expanded) => setVisualDesignerExpanded(expanded)}
              >
                <AccordionSummary sx={{ px: 2, py: 1.25 }}>
                  <Typography level="title-sm">Visual Designer</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pb: 2 }}>
                  {/* Survey-scoped VD editor (autosaves to visual_designer_survey_items). */}
                  <WorkspaceVisualDesignerCard
                    projectId={project.id}
                    customerId={project.customer_id}
                    defaultReference={defaultReference}
                    lockedSurveyId={survey.id}
                    lockRefreshKey={lockRefreshKey}
                    quoteEditing={showEditor}
                    finalHistorical={kind === 'final' && finalBalanceIssued}
                  />
                </AccordionDetails>
              </Accordion>
            </AccordionGroup>
          </CardContent>
        </Card>

        {showEditor && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{
              width: '100%',
              justifyContent: 'flex-end',
              gap: 1,
              pt: 1,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Button
              variant="soft"
              onClick={() => wizardLeaveRef.current?.openHelp()}
            >
              Help?
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                requestLeave(() => {
                  if (panel.type === 'edit') setPanel({ type: 'view', id: panel.id });
                });
              }}
            >
              Cancel
            </Button>
            <Button
              loading={pageSaving}
              onClick={() => {
                void (async () => {
                  setPageSaving(true);
                  try {
                    const ok = await wizardLeaveRef.current?.save();
                    if (ok === false) return;
                  } finally {
                    setPageSaving(false);
                  }
                })();
              }}
            >
              Save Changes
            </Button>
          </Stack>
        )}
      </Stack>
    );
  };

  const quoteWorkspacePanes = (
    <>
      {(showFinalTab ? quotePane : 'first') === 'first' && renderSurveyWorkspace('first')}
      {(showFinalTab ? quotePane : 'first') === 'final' && renderSurveyWorkspace('final')}
    </>
  );

  const defaultDocumentTitle = draftQuoteTitle(project.project_name, customer?.name);

  const documentPanel = (
    <>
      {panel.type === 'create' && (
        <Stack gap={2} sx={{ width: '100%' }}>
          <Card variant="outlined">
            <CardContent>
              <EstimateQuoteWizard
                key={`create-${panel.kind}-${panel.lifecycleRef || 'new'}-${panel.invoiceRole || 'plain'}`}
                embedded
                initialKind={panel.kind}
                projectId={project.id}
                workspaceProject={project}
                customerId={project.customer_id}
                preferredLifecycleRef={panel.lifecycleRef || project.lifecycle_ref || undefined}
                preferredTitle={panel.preferredTitle || defaultDocumentTitle}
                preferredInvoiceRole={panel.invoiceRole}
                preferredSeedLines={panel.seedLines}
                lockLifecycleRef
                lockCustomer
                lockKind
                versionLayout="vertical"
                hideVersions
                leaveGuardRef={wizardLeaveRef}
                onClose={() => setPanel({ type: 'idle' })}
                onDocumentReady={handleDocumentReady}
                {...visualDesignerBridge}
              />
            </CardContent>
          </Card>
          {firstSurvey && (
            <Card variant="outlined" sx={{ width: '100%' }}>
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <AccordionGroup>
                  <Accordion
                    expanded={visualDesignerExpanded}
                    onChange={(_, expanded) => setVisualDesignerExpanded(expanded)}
                  >
                    <AccordionSummary sx={{ px: 2, py: 1.25 }}>
                      <Typography level="title-sm">Visual Designer</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 2, pb: 2 }}>
                      <WorkspaceVisualDesignerCard
                        projectId={project.id}
                        customerId={project.customer_id}
                        defaultReference={defaultReference}
                        lockedSurveyId={firstSurvey.id}
                        lockRefreshKey={lockRefreshKey}
                        quoteEditing
                      />
                    </AccordionDetails>
                  </Accordion>
                </AccordionGroup>
              </CardContent>
            </Card>
          )}
          <Card variant="outlined">
            <CardContent>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{
                  width: '100%',
                  justifyContent: 'flex-end',
                  gap: 1,
                }}
              >
                <Button
                  variant="soft"
                  onClick={() => wizardLeaveRef.current?.openHelp()}
                >
                  Help?
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    requestLeave(() => setPanel({ type: 'idle' }));
                  }}
                >
                  Cancel
                </Button>
                <Button
                  loading={pageSaving}
                  onClick={() => {
                    void (async () => {
                      setPageSaving(true);
                      try {
                        const ok = await wizardLeaveRef.current?.save();
                        if (ok === false) return;
                      } finally {
                        setPageSaving(false);
                      }
                    })();
                  }}
                >
                  Create
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      )}
      {quoteWorkspaceOpen && (
        <Stack gap={1.5} sx={{ width: '100%' }}>
          <Tabs
            value={showFinalTab ? quotePane : 'first'}
            onChange={(_, value) => {
              requestLeave(() => {
                setVisualDesignerExpanded(false);
                setQuotePane((value as QuotePane) || 'first');
              });
            }}
          >
            <TabList
              sx={{
                bgcolor: 'transparent',
                '--TabList-gap': '4px',
                [`& .MuiTab-root.Mui-selected`]: {
                  bgcolor: 'var(--jarvis-selection-bg)',
                  color: 'var(--jarvis-selection-text)',
                  fontWeight: 700,
                },
                [`& .MuiTab-root.Mui-selected:hover`]: {
                  bgcolor: 'var(--jarvis-selection-bg)',
                  color: 'var(--jarvis-selection-text)',
                },
              }}
            >
              <Tab value="first">
                <Stack direction="row" alignItems="center" gap={0.75} component="span">
                  First Survey
                  <Chip size="sm" variant="soft" color={firstTabStatus.color} sx={{ pointerEvents: 'none' }}>
                    {firstTabStatus.label}
                  </Chip>
                </Stack>
              </Tab>
              {showFinalTab && (
                <Tab value="final">
                  <Stack direction="row" alignItems="center" gap={0.75} component="span">
                    Final Survey
                    <Chip size="sm" variant="soft" color={finalTabStatus.color} sx={{ pointerEvents: 'none' }}>
                      {finalTabStatus.label}
                    </Chip>
                  </Stack>
                </Tab>
              )}
            </TabList>
          </Tabs>
          {quoteWorkspacePanes}
        </Stack>
      )}
      {(panel.type === 'view' || panel.type === 'edit') && !quoteWorkspaceOpen && (
        <Stack gap={2} sx={{ width: '100%' }}>
          <Card variant="outlined" sx={{ width: '100%' }}>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              <AccordionGroup>
                <Accordion
                  expanded={invoiceDetailsExpanded}
                  onChange={(_, expanded) => setInvoiceDetailsExpanded(expanded)}
                >
                  <AccordionSummary sx={{ px: 2, py: 1.25 }}>
                    <Typography level="title-sm">Invoice Details</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2, pb: 2 }}>
                    {panel.type === 'edit' ? (
                      <EstimateQuoteWizard
                        key={`edit-${panel.id}`}
                        embedded
                        isEditMode
                        documentId={panel.id}
                        projectId={project.id}
                        workspaceProject={project}
                        customerId={project.customer_id}
                        lockCustomer
                        lockKind
                        versionLayout="vertical"
                        hideVersions
                        lockLifecycleRef
                        lockRefreshKey={lockRefreshKey}
                        leaveGuardRef={wizardLeaveRef}
                        onClose={() => setPanel({ type: 'view', id: panel.id })}
                        onDocumentReady={handleDocumentReady}
                        {...visualDesignerBridge}
                      />
                    ) : (
                      <DocumentDetailPage
                        key={`view-${panel.id}`}
                        documentId={panel.id}
                        embedded
                        allowDelete={(() => {
                          const viewing = invoices.find((invoice) => invoice.id === panel.id);
                          return !viewing || invoiceDeleteAllowedWithSchedule({
                            invoice: viewing,
                            invoices,
                            stages: paymentSchedule?.stages,
                          });
                        })()}
                        onClose={() => setPanel({ type: 'idle' })}
                        onDeleted={() => {
                          setQuotePane('first');
                          setPanel({ type: 'idle' });
                          void load();
                          void refreshSurveys();
                        }}
                        onEdit={(documentId) => {
                          setPanel({ type: 'edit', id: documentId });
                        }}
                        exportPdfSignal={panel.id === exportPdfDocId ? exportPdfSignal : 0}
                        onExportPdfTriggered={handleExportPdfTriggered}
                        onDocumentChanged={(documentId) => {
                          setPanel({ type: 'view', id: documentId });
                          void load();
                          void refreshSurveys();
                        }}
                      />
                    )}
                  </AccordionDetails>
                </Accordion>
              </AccordionGroup>
            </CardContent>
          </Card>
          {panel.type === 'edit' && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{
                width: '100%',
                justifyContent: 'flex-end',
                gap: 1,
                pt: 1,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Button
                variant="soft"
                onClick={() => wizardLeaveRef.current?.openHelp()}
              >
                Help?
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  requestLeave(() => {
                    setPanel({ type: 'view', id: panel.id });
                  });
                }}
              >
                Cancel
              </Button>
              <Button
                loading={pageSaving}
                onClick={() => {
                  void (async () => {
                    setPageSaving(true);
                    try {
                      const ok = await wizardLeaveRef.current?.save();
                      if (ok === false) return;
                    } finally {
                      setPageSaving(false);
                    }
                  })();
                }}
              >
                Save Changes
              </Button>
            </Stack>
          )}
        </Stack>
      )}
    </>
  );

  return (
    <Stack gap={2}>
      {error && <Alert color="danger">{error}</Alert>}
      {!project.customer_id && (
        <Alert color="warning">Assign a customer in Overview. New documents inherit that customer.</Alert>
      )}

      <Box sx={{ display: 'none' }}>
        <DocumentLinker ref={quoteLinkerRef} projectId={project.id} hideHeader onLinkCreated={load} />
        <InvoiceLinker ref={invoiceLinkerRef} projectId={project.id} hideHeader hideLinkedInvoices onLinkCreated={load} />
      </Box>

      <Card variant="outlined">
        <CardContent>
          <WorkspaceCardHeader
            icon={MdOutlineDescription}
            title={isQuotesSection ? 'Quotes / Estimates' : 'Invoices'}
            description={
              isQuotesSection
                ? 'One quote per workspace. First Survey is editable until transmitted. Acceptance on this row is First Survey. Final Survey has its own status and appears after First is accepted or Cash, or after an accepted estimate rolls to a draft quote.'
                : standaloneInvoicing
                  ? 'No quote or estimate on this workspace. Raise a Deposit or Full / works invoice, then send each to FreeAgent from this tab.'
                  : finalBalanceIssued
                  ? 'Final Balance is issued. Further invoices are a Variation or Credit only.'
                  : 'Deposit first, then the balance after Final settles. Send each invoice to FreeAgent from this tab.'
            }
            action={!isQuotesSection ? (
              <Button
                size="sm"
                startDecorator={<IconWrapper icon={FaPlus} />}
                disabled={!project.customer_id || creatingInvoice || !invoiceCreateButton.enabled}
                loading={creatingInvoice}
                onClick={() => {
                  if (standaloneInvoicing) {
                    setStandaloneChoiceOpen(true);
                    return;
                  }
                  if (!invoiceCreateButton.postSettlement) return;
                  setAdjustmentChoiceOpen(true);
                }}
              >
                {invoiceCreateButton.label}
              </Button>
            ) : undefined}
          />
          {loading && !loadedOnce && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size="sm" />
            </Box>
          )}

          {!isQuotesSection && loadedOnce && (
            <InvoiceSequencePanel
              invoices={invoices}
              finalSettled={finalSettled}
              nextRole={invoiceLedger.nextRole}
              creatingInvoice={creatingInvoice}
              firstAccepted={
                isAcceptedOrCashStatus(latestQuote?.status)
                || isAcceptedOrCashStatus(latestDocument(estimates)?.status)
                || firstIsHistorical
              }
              quoteHasDeposit={
                !!(linkedQuote?.hasDeposit && Number(linkedQuote.depositAmount || 0) > 0)
                || !!(latestDocument(estimates)?.hasDeposit && Number(latestDocument(estimates)?.depositAmount || 0) > 0)
              }
              selectedId={selectedId}
              onSelect={toggleView}
              onView={(doc) => changePanel({ type: 'view', id: doc.id })}
              onEdit={(doc) => changePanel({ type: 'edit', id: doc.id })}
              onExportPdf={requestExportPdf}
              onDelete={(doc) => { void deleteInvoiceDocument(doc); }}
              onReissueBalance={requestReissueBalance}
              onCreateCredit={() => startPostSettlementInvoice('credit')}
              finalBalanceIssued={finalBalanceIssued}
              onSaveDeductions={saveBalanceDeductions}
              balanceAdjustments={balanceAdjustments}
              onIssueBalance={() => { void createInvoiceFromQuote(linkedQuote?.id); }}
              onCreateDeposit={acceptedDepositSource ? () => { void createMissingDepositInvoice(); } : undefined}
              onChangeStatus={(doc, status) => { void updateDocStatus(doc, status); }}
              statusBusyId={statusBusyId}
              onDocumentsChanged={() => { void load(); }}
              anticipatedBalance={invoiceLedger.delta}
              agreedQuoteTotal={standaloneInvoicing ? null : agreedGross}
              scheduleStages={presentedScheduleStages}
              scheduleRawStages={paymentSchedule?.stages || null}
              plainList={standaloneInvoicing}
            />
          )}

          <Stack sx={{ display: isQuotesSection ? 'flex' : 'none' }}>
            {lanes.map((lane, index) => {
              const latest = latestDocument(lane.docs);
              const extraCount = lane.kind === 'Estimate' ? Math.max(0, lane.docs.length - (latest ? 1 : 0)) : 0;
              const docsToShow = lane.kind === 'Invoice'
                ? [...lane.docs].sort((a, b) => (Date.parse(b.updatedAt || b.createdAt) || 0) - (Date.parse(a.updatedAt || a.createdAt) || 0))
                : (latest ? [latest] : []);
              return (
                <Box key={lane.kind} sx={{ display: 'flex', gap: 1.5, minHeight: 88 }}>
                  <Box
                    sx={{
                      width: 44,
                      flexShrink: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      pt: 1.25,
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 'md',
                        bgcolor: 'var(--jarvis-card-icon-bg)',
                        color: 'var(--jarvis-card-icon-color)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1,
                      }}
                    >
                      <IconWrapper icon={lane.icon} />
                    </Box>
                    {index < lanes.length - 1 && (
                      <Box sx={{ width: 2, flex: 1, bgcolor: 'primary.200', my: 0.5, borderRadius: 1 }} />
                    )}
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: 'stretch',
                      gap: 1.5,
                      py: 1.25,
                      minHeight: 108,
                      borderBottom: index < lanes.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                    }}
                  >
                    <Box sx={{ width: { xs: 'auto', sm: 110, md: 170 }, flexShrink: 0, display: 'flex', flexDirection: 'column', justifyContent: docsToShow.length > 1 ? 'flex-start' : 'center', pt: { xs: 0.5, sm: docsToShow.length > 1 ? 0.5 : 0 } }}>
                      <Typography level="title-sm">{lane.title}</Typography>
                      <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                        {lane.description}
                      </Typography>
                    </Box>

                    {docsToShow.length > 0 ? (
                      <Stack gap={1} sx={{ flex: 1, minWidth: 0 }}>
                        {docsToShow.map((doc) => {
                          const progressed = doc.kind === 'Estimate' && quoteRefs.has(doc.lifecycleRef);
                          const meta = statusMeta(
                            doc,
                            doc.kind === 'Quotation' ? commercialFinalAcceptance : undefined,
                          );
                          const versionInfo = doc.kind === 'Invoice' ? undefined : versionById[doc.id];
                          const { subtitleTitle } = documentIdentity(doc, versionInfo?.title);
                          const subtitleBits = [
                            subtitleTitle,
                            progressed ? `superseded by quote ${doc.lifecycleRef}` : null,
                            extraCount > 0 ? `+${extraCount} more` : null,
                          ].filter(Boolean);
                          const rowSelected = selectedId === doc.id;
                          const rowMuted = rowSelected
                            ? 'var(--jarvis-selection-text)'
                            : 'neutral.500';
                          return (
                            <Box
                              key={doc.id}
                              onClick={() => toggleView(doc)}
                              role="button"
                              tabIndex={0}
                              aria-expanded={rowSelected}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  toggleView(doc);
                                }
                              }}
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                  xs: 'minmax(0, 1fr) auto',
                                  sm: 'minmax(140px, 200px) minmax(0, 1fr) auto auto',
                                },
                                gridTemplateAreas: {
                                  xs: '"ref amount" "status menu"',
                                  sm: '"ref status amount menu"',
                                },
                                columnGap: { xs: 1.5, sm: 2.5 },
                                rowGap: 0.75,
                                alignItems: 'center',
                                cursor: 'pointer',
                                borderRadius: 'sm',
                                px: 1.25,
                                py: 1,
                                bgcolor: rowSelected ? 'var(--jarvis-selection-bg)' : 'transparent',
                                color: rowSelected ? 'var(--jarvis-selection-text)' : 'inherit',
                              }}
                            >
                              <Box sx={{ gridArea: 'ref', minWidth: 0 }}>
                                <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
                                  <Typography level="body-sm" fontWeight="lg" sx={{ color: 'inherit' }}>
                                    {documentDisplayRef(doc)}
                                  </Typography>
                                  {doc.kind === 'Invoice' && (
                                    <Chip size="sm" variant="soft" color={normalizeInvoiceRole(doc.invoiceRole) === 'credit' ? 'warning' : 'primary'}>
                                      {invoiceRoleLabel(doc.invoiceRole)}
                                    </Chip>
                                  )}
                                </Stack>
                                {subtitleBits.length > 0 && (
                                  <Typography level="body-xs" sx={{ color: rowMuted, opacity: rowSelected ? 0.85 : 1 }} noWrap>
                                    {subtitleBits.join(' · ')}
                                  </Typography>
                                )}
                              </Box>
                              <Box sx={{ gridArea: 'status', minWidth: 0 }}>
                                <Box
                                  sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', maxWidth: '100%' }}
                                  onClick={(event) => event.stopPropagation()}
                                  onKeyDown={(event) => event.stopPropagation()}
                                >
                                <Dropdown>
                                  <MenuButton
                                    slots={{ root: Chip }}
                                    slotProps={{
                                      root: {
                                        size: 'sm',
                                        variant: 'soft',
                                        color: meta.color,
                                        disabled: statusBusyId === doc.id,
                                        startDecorator: meta.icon ? <IconWrapper icon={meta.icon} /> : undefined,
                                        sx: { cursor: 'pointer', maxWidth: '100%' },
                                        'aria-label': `Change status (${meta.label})`,
                                      },
                                    }}
                                  >
                                    {meta.label}
                                  </MenuButton>
                                  <Menu placement="bottom-start" size="sm">
                                    {getAvailableStatuses(doc.kind).map((status) => (
                                      <MenuItem
                                        key={status}
                                        selected={String(doc.status || '').trim().toLowerCase() === status}
                                        disabled={statusBusyId === doc.id}
                                        onClick={() => { void updateDocStatus(doc, status); }}
                                      >
                                        {status.charAt(0).toUpperCase() + status.slice(1)}
                                      </MenuItem>
                                    ))}
                                  </Menu>
                                </Dropdown>
                                {meta.caption && (
                                  <Typography level="body-xs" sx={{ color: rowMuted, opacity: rowSelected ? 0.85 : 1, mt: 0.35 }}>
                                    {meta.caption}
                                  </Typography>
                                )}
                                </Box>
                              </Box>
                              <Box sx={{ gridArea: 'amount', textAlign: 'right', flexShrink: 0, minWidth: 104 }}>
                                <Typography level="title-sm" sx={{ color: 'inherit' }}>{formatMoney(doc.grossTotal)}</Typography>
                                <Typography level="body-xs" sx={{ color: rowMuted, opacity: rowSelected ? 0.85 : 1 }}>
                                  {kindNoun(doc.kind)}
                                </Typography>
                              </Box>
                              <Stack
                                direction="row"
                                gap={0.5}
                                alignItems="center"
                                sx={{ gridArea: 'menu', color: 'inherit' }}
                                onClick={(event) => event.stopPropagation()}
                              >
                                <Dropdown>
                                  <MenuButton
                                    slots={{ root: IconButton }}
                                    slotProps={{
                                      root: {
                                        size: 'sm',
                                        variant: 'plain',
                                        'aria-label': `${lane.title} actions`,
                                        sx: rowSelected
                                          ? { color: 'var(--jarvis-selection-text)' }
                                          : undefined,
                                      },
                                    }}
                                  >
                                    <IconWrapper icon={FaEllipsisV} />
                                  </MenuButton>
                                  <Menu placement="bottom-end">
                                    <MenuItem onClick={() => changePanel({ type: 'view', id: doc.id })}>View</MenuItem>
                                    <MenuItem onClick={() => {
                                      if (isQuotesSection && doc.kind === 'Quotation') {
                                        setQuotePane(showFinalTab ? 'final' : 'first');
                                      }
                                      changePanel({ type: 'edit', id: doc.id });
                                    }}>Edit</MenuItem>
                                    {doc.kind === 'Quotation' && (
                                      <MenuItem
                                        disabled={sendingQuote || !customer?.email || firstSurveySendLocked}
                                        onClick={() => sendQuote(doc)}
                                      >
                                        {firstIsHistorical
                                          ? 'Send First Survey (already sent)'
                                          : 'Send First Survey'}
                                      </MenuItem>
                                    )}
                                    {doc.kind === 'Quotation' && finalSettled && (
                                      <MenuItem
                                        disabled={creatingInvoice}
                                        onClick={() => requestCreateInvoice(doc.id)}
                                      >
                                        Issue balance invoice
                                      </MenuItem>
                                    )}
                                    {versionInfo?.isTransmitted && doc.kind !== 'Quotation' && (
                                      <MenuItem
                                        disabled={lockBusy}
                                        onClick={() => openLockDialog(doc, versionInfo.overrideUnlocked ? 'relock' : 'unlock')}
                                      >
                                        {versionInfo.overrideUnlocked ? 'Relock sent quote' : 'Unlock override'}
                                      </MenuItem>
                                    )}
                                    <MenuItem onClick={() => requestExportPdf(doc)}>Export PDF</MenuItem>
                                    {doc.kind === 'Estimate' && canProgressUnsentEstimate({
                                      estimateStatus: doc.status,
                                      hasQuote: quotes.length > 0,
                                      alreadyProgressed: progressed,
                                      firstHasBeenTransmitted: firstIsHistorical,
                                    }) && (
                                      <MenuItem
                                        disabled={assigningId === doc.id}
                                        onClick={() => { void progressEstimate(doc); }}
                                      >
                                        Progress to quote
                                      </MenuItem>
                                    )}
                                    {(doc.kind === 'Quotation' || doc.kind === 'Estimate') && !firstIsHistorical && (
                                      <MenuItem
                                        color="danger"
                                        disabled={deletingDocId === doc.id || assigningId === doc.id}
                                        onClick={() => { void deleteCommercialDocument(doc); }}
                                      >
                                        {doc.kind === 'Quotation' ? 'Delete quote' : 'Delete estimate'}
                                      </MenuItem>
                                    )}
                                    {doc.kind === 'Invoice' && invoiceDeleteAllowedWithSchedule({
                                      invoice: doc,
                                      invoices,
                                      stages: paymentSchedule?.stages,
                                    }) && (
                                      <MenuItem
                                        color="danger"
                                        disabled={deletingDocId === doc.id || assigningId === doc.id}
                                        onClick={() => { void deleteInvoiceDocument(doc); }}
                                      >
                                        Delete invoice
                                      </MenuItem>
                                    )}
                                  </Menu>
                                </Dropdown>
                              </Stack>
                            </Box>
                          );
                        })}
                        {lane.kind === 'Invoice' && (
                          <Button
                            size="sm"
                            onClick={() => requestCreateInvoice()}
                            disabled={!project.customer_id || creatingInvoice}
                            startDecorator={<IconWrapper icon={FaPlus} />}
                            sx={{
                              alignSelf: 'flex-end',
                              minWidth: 152,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Issue balance
                          </Button>
                        )}
                      </Stack>
                    ) : (
                      <>
                        <Box
                          sx={{
                            flex: 1,
                            minWidth: { xs: 0, sm: 180 },
                            minHeight: 76,
                            border: '1px dashed',
                            borderColor: 'neutral.300',
                            borderRadius: 'md',
                            px: 1.5,
                            py: 1.25,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography level="body-sm" fontWeight="lg">{lane.emptyTitle}</Typography>
                          <Typography level="body-xs" sx={{ color: 'neutral.500' }}>{lane.emptyBody}</Typography>
                        </Box>
                        {!(lane.kind === 'Estimate' && quoteBlocksEstimate) && (
                          <Button
                            size="sm"
                            onClick={() => (lane.kind === 'Invoice' ? requestCreateInvoice() : startCreate(lane.kind))}
                            disabled={!project.customer_id || billingBlocked || (lane.kind === 'Invoice' && creatingInvoice)}
                            loading={lane.kind === 'Invoice' ? creatingInvoice : false}
                            startDecorator={<IconWrapper icon={FaPlus} />}
                            sx={{
                              flexShrink: 0,
                              alignSelf: { xs: 'flex-start', sm: 'center' },
                              minWidth: 152,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {lane.createLabel}
                          </Button>
                        )}
                      </>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </CardContent>
      </Card>

      {!quoteWorkspaceOpen && panel.type === 'idle' && (
        <WorkspaceTransmissionsCard
          documents={isQuotesSection ? [...estimates, ...quotes] : invoices}
          versionById={versionById}
          firstWorkingVersionId={firstSurvey?.documentVersionId || null}
          finalWorkingVersionId={finalSurvey?.documentVersionId || null}
          finalSurveyId={finalSurvey?.id || null}
          finalReapprovalTransmissionVersionId={finalDelta?.reapprovalTransmissionVersionId || null}
          showMismatchAlert={false}
          refreshKey={`${loading}-${sendingQuote}`}
          onDocumentsChanged={() => { void load(); }}
        />
      )}

      {documentPanel}

      <Modal open={adjustmentChoiceOpen} onClose={() => setAdjustmentChoiceOpen(false)}>
        <ModalDialog sx={{ maxWidth: 480, width: '100%' }}>
          <ModalClose />
          <DialogTitle>Create Variation/Credit</DialogTitle>
          <Typography level="body-sm" sx={{ color: 'neutral.600', mb: 1.5 }}>
            Final Balance is already issued. Raise a later adjustment — not another Balance.
          </Typography>
          <Stack gap={1.25}>
            <Button
              variant="outlined"
              color="neutral"
              onClick={() => startPostSettlementInvoice('variation')}
              sx={{
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                textAlign: 'left',
                py: 1.5,
                height: 'auto',
                whiteSpace: 'normal',
              }}
            >
              <Stack gap={0.25} sx={{ width: '100%' }}>
                <Typography level="title-sm">Create Variation</Typography>
                <Typography level="body-xs" sx={{ color: 'neutral.500', fontWeight: 'normal' }}>
                  Additional charge after settlement. Enter the amount on the next screen.
                </Typography>
              </Stack>
            </Button>
            <Button
              variant="outlined"
              color="neutral"
              onClick={() => startPostSettlementInvoice('credit')}
              sx={{
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                textAlign: 'left',
                py: 1.5,
                height: 'auto',
                whiteSpace: 'normal',
              }}
            >
              <Stack gap={0.25} sx={{ width: '100%' }}>
                <Typography level="title-sm">Create Credit</Typography>
                <Typography level="body-xs" sx={{ color: 'neutral.500', fontWeight: 'normal' }}>
                  Reduce previously invoiced totals. Enter the amount on the next screen.
                </Typography>
              </Stack>
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>

      <Modal open={standaloneChoiceOpen} onClose={() => setStandaloneChoiceOpen(false)}>
        <ModalDialog sx={{ maxWidth: 480, width: '100%' }}>
          <ModalClose />
          <DialogTitle>Create Invoice</DialogTitle>
          <Typography level="body-sm" sx={{ color: 'neutral.600', mb: 1.5 }}>
            This workspace has no quote or estimate. Choose the invoice type.
          </Typography>
          <Stack gap={1.25}>
            {([
              { role: 'deposit', title: 'Deposit invoice', body: 'Up-front payment. Enter the amount on the next screen.' },
              { role: 'works', title: 'Full / works invoice', body: 'Charge for the work. Enter the lines and amounts on the next screen.' },
            ] as const).map((option) => (
              <Button
                key={option.role}
                variant="outlined"
                color="neutral"
                onClick={() => startStandaloneInvoice(option.role)}
                sx={{
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  textAlign: 'left',
                  py: 1.5,
                  height: 'auto',
                  whiteSpace: 'normal',
                }}
              >
                <Stack gap={0.25} sx={{ width: '100%' }}>
                  <Typography level="title-sm">{option.title}</Typography>
                  <Typography level="body-xs" sx={{ color: 'neutral.500', fontWeight: 'normal' }}>
                    {option.body}
                  </Typography>
                </Stack>
              </Button>
            ))}
          </Stack>
        </ModalDialog>
      </Modal>

      <Modal open={invoiceChoiceOpen} onClose={() => { if (!creatingInvoice) { setInvoiceChoiceOpen(false); setInvoiceSourceQuoteId(null); } }}>
        <ModalDialog sx={{ maxWidth: 480, width: '100%' }}>
          <ModalClose disabled={creatingInvoice} />
          <DialogTitle>Issue invoice</DialogTitle>
          <Typography level="body-sm" sx={{ color: 'neutral.600', mb: 1.5 }}>
            {linkedQuote
              ? `Issue the next invoice for ${linkedQuote.lifecycleRef} from the settled contract, or start a blank invoice.`
              : 'Start a new invoice linked to this workspace.'}
          </Typography>
          <Stack gap={1.25}>
            <Button
              variant="outlined"
              color="neutral"
              disabled={!linkedQuote || creatingInvoice}
              loading={creatingInvoice}
              onClick={() => { void createInvoiceFromQuote(invoiceSourceQuoteId || undefined); }}
              sx={{
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                textAlign: 'left',
                py: 1.5,
                height: 'auto',
                whiteSpace: 'normal',
              }}
            >
              <Stack gap={0.25} sx={{ width: '100%' }}>
                <Typography level="title-sm">Issue balance invoice</Typography>
                <Typography level="body-xs" sx={{ color: 'neutral.500', fontWeight: 'normal' }}>
                  Compare the settled contract with invoices already raised, then draft the remainder or a credit.
                </Typography>
              </Stack>
            </Button>
            <Button
              variant="outlined"
              color="neutral"
              disabled={creatingInvoice}
              onClick={() => startCreate('Invoice', quoteInvoiceExists ? undefined : linkedQuote?.lifecycleRef)}
              sx={{
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                textAlign: 'left',
                py: 1.5,
                height: 'auto',
                whiteSpace: 'normal',
              }}
            >
              <Stack gap={0.25} sx={{ width: '100%' }}>
                <Typography level="title-sm">Create new</Typography>
                <Typography level="body-xs" sx={{ color: 'neutral.500', fontWeight: 'normal' }}>
                  Start a blank invoice linked to this workspace{linkedQuote ? ` and ${linkedQuote.lifecycleRef}` : ''}.
                </Typography>
              </Stack>
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>

      <Modal open={!!lockDialog} onClose={closeLockDialog}>
        <ModalDialog sx={{ maxWidth: 440, width: '100%' }}>
          <ModalClose disabled={lockBusy} />
          <DialogTitle>
            {lockDialog?.mode === 'unlock' ? 'Unlock override' : 'Relock'}
          </DialogTitle>
          {lockDialog?.mode === 'unlock' ? (
            <>
              <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
                Unlock {lockDialog.label} for edits. A new working copy is created so any customer transmission stays unchanged. Save, then Relock when finished.
              </Typography>
              <FormControl sx={{ mt: 1.25 }}>
                <FormLabel>Reason (optional)</FormLabel>
                <Input
                  autoFocus
                  value={lockReason}
                  onChange={(event) => setLockReason(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') void submitLockDialog();
                  }}
                />
              </FormControl>
            </>
          ) : (
            <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
              Relock {lockDialog?.label}? The working copy will lock again. Existing customer transmissions stay unchanged.
            </Typography>
          )}
          <Stack direction="row" justifyContent="flex-end" gap={1} sx={{ mt: 1.5 }}>
            <Button variant="plain" color="neutral" disabled={lockBusy} onClick={closeLockDialog}>
              Cancel
            </Button>
            <Button
              color={lockDialog?.mode === 'relock' ? 'warning' : 'primary'}
              loading={lockBusy}
              onClick={() => { void submitLockDialog(); }}
            >
              {lockDialog?.mode === 'unlock' ? 'Unlock' : 'Relock'}
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>

      <Modal open={reviewDeltaOpen} onClose={() => { if (!reapprovalBusy) setReviewDeltaOpen(false); }}>
        <ModalDialog sx={{ maxWidth: 760, width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
          <ModalClose disabled={reapprovalBusy} />
          <DialogTitle>Review Changes</DialogTitle>
          <Typography level="body-sm" sx={{ color: 'neutral.700', mb: 1.25 }}>
            {finalSurveyReviewIntro(finalDelta?.snapshot?.comparedAgainst)}
          </Typography>
          {(finalDelta?.classification === 'pending_review' || finalDelta?.classification == null) && (
            <Typography level="body-xs" sx={{ color: 'neutral.500', mb: 1 }}>
              You can close this and decide later. Final Survey stays complete until you choose Yes or No, or Reopen it.
            </Typography>
          )}
          {finalDelta ? (
            <Stack gap={1.5}>
              <FinalSurveyReviewTable
                sections={reviewSectionsFromSnapshot(finalDelta.snapshot)}
                originalHeading={finalDelta.snapshot.comparedAgainst === 'previous_final' ? 'Previous' : 'Original'}
                emptyText={finalDelta.snapshot.comparedAgainst === 'previous_final'
                  ? 'No differences were found since the previous Final Survey.'
                  : undefined}
              />
              {(finalDelta.classification === 'pending_review' || finalDelta.classification == null) ? (
                <Stack gap={1} sx={{ mt: 0.5 }}>
                  <Typography level="title-sm">Do these changes require customer approval?</Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      color="neutral"
                      loading={reapprovalBusy}
                      onClick={() => { void decideFinalReapproval(false); }}
                    >
                      No — update without reapproval
                    </Button>
                    <Button
                      color="warning"
                      loading={reapprovalBusy}
                      onClick={() => { void decideFinalReapproval(true); }}
                    >
                      Yes — send for customer reapproval
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                <Stack gap={1} sx={{ mt: 0.5 }}>
                  <Alert color={finalDelta.classification === 'no_reapproval' ? 'success' : 'warning'}>
                    {finalDelta.classification === 'no_reapproval'
                      ? 'The customer’s live document has been updated. They will not be asked to approve these changes.'
                      : finalDelta.customerAcceptedAt
                        ? 'The customer accepted the updated details.'
                        : finalDelta.reapprovalTransmissionVersionId
                          ? 'The updated details have been sent to the customer for approval.'
                          : 'Customer approval is required. Send the updated details for approval next.'}
                  </Alert>
                  {finalDelta.classification === 'customer_reapproval_required'
                    && !finalDelta.customerAcceptedAt && (
                    <Button
                      color="warning"
                      loading={surveyActionBusy || reapprovalBusy}
                      onClick={() => { void transmitFinalForApproval(); }}
                    >
                      {finalDelta.reapprovalTransmissionVersionId
                        ? 'Retransmit for Approval'
                        : 'Transmit for Approval'}
                    </Button>
                  )}
                </Stack>
              )}
            </Stack>
          ) : (
            <Alert color="warning">No comparison is available yet. Mark the Final Survey complete to review changes.</Alert>
          )}
        </ModalDialog>
      </Modal>

      <Modal open={!!confirmDialog} onClose={() => setConfirmDialog(null)}>
        <ModalDialog sx={{ maxWidth: 480, width: '100%' }}>
          <ModalClose />
          <DialogTitle>{confirmDialog?.title}</DialogTitle>
          {confirmDialog?.alert && (
            <Alert color="danger" sx={{ mb: 1 }}>
              {confirmDialog.alert}
            </Alert>
          )}
          <Typography level="body-sm" sx={{ color: 'neutral.600', whiteSpace: 'pre-line' }}>
            {confirmDialog?.body}
          </Typography>
          <Stack direction="row" justifyContent="flex-end" gap={1} sx={{ mt: 1.5 }}>
            <Button variant="plain" color="neutral" onClick={() => setConfirmDialog(null)}>
              Cancel
            </Button>
            <Button
              color={confirmDialog?.confirmColor || 'primary'}
              onClick={() => {
                const action = confirmDialog?.onConfirm;
                setConfirmDialog(null);
                if (action) void action();
              }}
            >
              {confirmDialog?.confirmLabel || 'OK'}
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>

      <Modal open={!!noticeDialog} onClose={() => setNoticeDialog(null)}>
        <ModalDialog sx={{ maxWidth: 480, width: '100%' }}>
          <ModalClose />
          <DialogTitle>{noticeDialog?.title}</DialogTitle>
          <Typography level="body-sm" sx={{ color: 'neutral.600', whiteSpace: 'pre-line' }}>
            {noticeDialog?.body}
          </Typography>
          {noticeDialog?.url ? (
            <Link href={noticeDialog.url} target="_blank" rel="noopener noreferrer" level="body-sm" sx={{ mt: 1, fontWeight: 600 }}>
              Open customer link
            </Link>
          ) : null}
          <Stack direction="row" justifyContent="flex-end" gap={1} sx={{ mt: 1.5 }}>
            <Button onClick={() => setNoticeDialog(null)}>OK</Button>
          </Stack>
        </ModalDialog>
      </Modal>

      {!isQuotesSection && (
        <PaymentScheduleCard
          projectId={project.id}
          schedule={paymentSchedule}
          grossTotal={ledgerGross}
          invoices={scheduleInvoices}
          depositAmount={linkedQuote?.depositAmount ?? latestDocument(estimates)?.depositAmount}
          depositPercentage={linkedQuote?.depositPercentage ?? latestDocument(estimates)?.depositPercentage}
          quotedPattern={linkedQuote?.paymentStructurePattern ?? latestDocument(estimates)?.paymentStructurePattern}
          finalSettled={finalSettled}
          firstAccepted={
            isAcceptedOrCashStatus(linkedQuote?.status)
            || isAcceptedOrCashStatus(latestDocument(estimates)?.status)
            || firstIsHistorical
          }
          onChanged={() => {
            setScheduleTick((value) => value + 1);
            void load();
          }}
        />
      )}

      <ProjectFolderViewer project={project} />

      <Card variant="outlined">
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <AccordionGroup>
            <Accordion
              expanded={advancedLinkingOpen}
              onChange={(_, next) => setAdvancedLinkingOpen(Boolean(next))}
            >
              <AccordionSummary
                sx={{ px: 2, py: 1.25 }}
                slotProps={{ button: { sx: { gap: 1, alignItems: 'flex-start' } } }}
              >
                <Stack direction="row" alignItems="flex-start" gap={1} sx={{ flex: 1, pr: 1 }}>
                  <Box sx={{ color: 'primary.500', display: 'flex', mt: 0.25 }}>
                    <IconWrapper icon={FaLink} />
                  </Box>
                  <Box>
                    <Typography level="title-md">Advanced document linking</Typography>
                    <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                      Link unlinked documents for this customer. Adoption is the whole document, not a version.
                    </Typography>
                  </Box>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, pb: 2, display: advancedLinkingOpen ? 'block' : 'none' }}>
                {isAdmin ? (
                  <>
                    <Typography level="body-xs" sx={{ color: 'neutral.500', mb: 1.5 }}>
                      Boundary case only. {isQuotesSection
                        ? 'A workspace can have one linked quote. Use this to adopt an orphaned customer document that is not on another workspace. Prefer Clone workspace when you need a new lifecycle ref.'
                        : 'Invoices already on another workspace cannot be moved here.'}
                    </Typography>
                    <Stack direction="row" gap={1} sx={{ mb: 1.5 }} flexWrap="wrap">
                      {isQuotesSection ? (
                        <Button size="sm" variant="outlined" onClick={() => quoteLinkerRef.current?.openModal()}>
                          Link quote / estimate
                        </Button>
                      ) : (
                        <Button size="sm" variant="outlined" onClick={() => invoiceLinkerRef.current?.openModal()}>
                          Link invoice
                        </Button>
                      )}
                    </Stack>
                    <Divider sx={{ mb: 1.5 }} />
                    {(isQuotesSection ? candidateQuotes.length === 0 : candidateInvoices.length === 0) && !loading && (
                      <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                        No unlinked documents for this customer.
                      </Typography>
                    )}
                    {isQuotesSection && candidateQuotes.length > 0 && (
                      <Stack gap={1} sx={{ mb: candidateInvoices.length ? 2 : 0 }}>
                        <Typography level="body-xs" sx={{ color: 'neutral.500' }}>Quotes / Estimates</Typography>
                        {candidateQuotes.map((doc) => (
                          <CandidateRow
                            key={doc.id}
                            doc={doc}
                            versionTitle={versionById[doc.id]?.title}
                            trailing={
                              <Button
                                size="sm"
                                loading={assigningId === doc.id}
                                disabled={
                                  (doc.kind === 'Estimate' && (quoteBlocksEstimate || estimates.length > 0))
                                  || (doc.kind === 'Quotation' && quotes.length > 0)
                                }
                                onClick={() => assignToProject(doc)}
                              >
                                Adopt
                              </Button>
                            }
                          />
                        ))}
                      </Stack>
                    )}
                    {!isQuotesSection && candidateInvoices.length > 0 && (
                      <Stack gap={1}>
                        <Typography level="body-xs" sx={{ color: 'neutral.500' }}>Invoices</Typography>
                        {candidateInvoices.map((doc) => (
                          <CandidateRow
                            key={doc.id}
                            doc={doc}
                            trailing={
                              <Button size="sm" loading={assigningId === doc.id} onClick={() => assignToProject(doc)}>
                                Adopt
                              </Button>
                            }
                          />
                        ))}
                      </Stack>
                    )}
                  </>
                ) : (
                  <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                    Admin access is required to adopt unlinked documents.
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          </AccordionGroup>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default QuotesStagePanel;
